import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  FiX,
  FiMap,
  FiSend,
  FiFileText,
  FiMail,
  FiChevronLeft,
  FiChevronRight,
  FiPlus,
  FiTrash2,
  FiTarget,
  FiList,
  FiCheck,
  FiCheckSquare,
  FiInfo,
  FiChevronDown
} from 'react-icons/fi';
import moment from 'moment';
import Surface from '../ui/Surface';
import Button from '../ui/Button';
import IconButton from '../ui/IconButton';
import { useQuotesBookings } from '../quotes/QuotesBookingsContext';
import TimeDropdownInput from '../quotes/TimeDropdownInput';
import LocationSelector from '../quotes/LocationSelector';
import styles from './LoadWorkspace.module.css';
import { getReferenceValue } from '../../utils/shipmentReferences';
import apiClient, { withApiPath } from '../../utils/apiClient';
import useDebouncedBoundaryWrites from './useDebouncedBoundaryWrites';
import { shouldShowManualSync } from './useLoadWorkspaceSync';
import DocumentsConsole from './DocumentsConsole';

export const ShipmentTimeline = ({ timeline, loading, error, onReturn }) => (
  <section className={styles.timelinePanel} aria-label="Shipment timeline">
    <div className={styles.timelinePanelHeader}><h3>ACTIVITY</h3><button type="button" onClick={onReturn}>Return to shipment</button></div>
    {loading && <p>Loading activity…</p>}
    {error && <p className={styles.errorText}>{error}</p>}
    {!loading && !error && timeline.map((entry) => (
      <div className={styles.timelineEntry} key={entry.id}>
        <time title={entry.created_at}>{moment(entry.created_at).format('MMM D · h:mm A')}</time>
        <strong title={entry.actor_name}>{entry.actor_name}</strong>
        <span title={entry.summary}>{entry.summary}</span>
        <small>{entry.event_type === 'remark' ? 'REMARK' : entry.event_type === 'user_change' ? 'USER CHANGE' : 'SYSTEM'}</small>
      </div>
    ))}
    {!loading && !error && !timeline.length && <p>No recorded changes yet.</p>}
  </section>
);

export const EquipmentNotesModal = ({ equipmentType, initialValue, onCancel, onSave }) => {
  const [draft, setDraft] = useState(initialValue || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const isDirty = draft !== (initialValue || '');

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && !isDirty && !saving) onCancel();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isDirty, onCancel, saving]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await onSave(draft);
    } catch (_) {
      setError('Equipment notes could not be saved. Try again.');
      setSaving(false);
    }
  };

  return createPortal(
    <div className={styles.modalBackdrop}>
      <div className={`${styles.modal} ${styles.equipmentNotesModal}`} role="dialog" aria-modal="true" aria-labelledby="equipment-notes-title">
        <header className={styles.modalHeader}>
          <div>
            <h3 id="equipment-notes-title">Equipment notes</h3>
            <span className={styles.modalHint}>{equipmentType}</span>
          </div>
        </header>
        <div className={styles.equipmentNotesBody}>
          <label className={styles.fieldLabel}>
            Notes
            <textarea
              autoFocus
              rows={5}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
            />
          </label>
          {error && <p className={styles.inlineError} role="alert">{error}</p>}
          <div className={styles.equipmentNotesActions}>
            <Button variant="ghost" onClick={onCancel} disabled={saving}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export const RouteOverviewTimeline = ({ stops, etaNodeIndex = -1, miles }) => (
  <div className={styles.timelineScroll} data-testid="route-overview-timeline">
    {stops.map((stop, index) => {
      const isLast = index === stops.length - 1;
      const dotClasses = [styles.timelineDot];
      if (stop.statusVariant === 'half') dotClasses.push(styles.timelineDotHalf);
      else if (stop.statusVariant === 'filled') dotClasses.push(styles.timelineDotFilled);
      return (
        <React.Fragment key={`${stop.type}-${index}`}>
          <div className={styles.timelineItem}>
            <div className={styles.timelineNode}>
              <span className={dotClasses.join(' ')} />
              <div className={styles.timelineLabel} data-testid={`route-endpoint-${index}`}>
                <span className={styles.timelineCity} title={stop.cityState}>{stop.cityState}</span>
                {index === etaNodeIndex && <span className={styles.timelineEta}>ETA —</span>}
                {stop.scheduleDisplay && (
                  <span className={styles.timelineSchedule} title={stop.scheduleDisplay}>{stop.scheduleDisplay}</span>
                )}
              </div>
            </div>
          </div>
          {!isLast && (
            <div className={styles.timelineConnector} data-testid="route-center">
              <span className={styles.timelineSpine} />
              <span className={styles.timelineMiles}>{stops.length === 2 && miles ? `${miles} mi` : '— mi'}</span>
              <span className={styles.timelineArrow}>→</span>
            </div>
          )}
        </React.Fragment>
      );
    })}
  </div>
);

export const EquipmentNotesButton = ({ equipmentType, hasNotes, disabled, onClick }) => (
  <button
    type="button"
    className={`${styles.notesToggle} ${hasNotes ? styles.notesToggleActive : ''}`}
    onClick={onClick}
    aria-label={`Equipment notes for ${equipmentType}`}
    aria-pressed={hasNotes}
    title="Equipment notes"
    disabled={disabled}
  >
    <FiFileText aria-hidden="true" />
    {hasNotes && <span className={styles.notesIndicator} aria-hidden="true" />}
  </button>
);

const TIMEZONE_ABBREVIATION_MAP = {
  EST: 'America/New_York',
  EDT: 'America/New_York',
  ET: 'America/New_York',
  CST: 'America/Chicago',
  CDT: 'America/Chicago',
  CT: 'America/Chicago',
  MST: 'America/Denver',
  MDT: 'America/Denver',
  MT: 'America/Denver',
  PST: 'America/Los_Angeles',
  PDT: 'America/Los_Angeles',
  PT: 'America/Los_Angeles',
  UTC: 'UTC',
  GMT: 'UTC'
};

const normalizeTimeZoneId = (value) => {
  if (!value) return '';
  const trimmed = value.trim();
  if (TIMEZONE_ABBREVIATION_MAP[trimmed.toUpperCase()]) {
    return TIMEZONE_ABBREVIATION_MAP[trimmed.toUpperCase()];
  }
  if (/^[A-Za-z]+\/[A-Za-z_]+/.test(trimmed)) {
    return trimmed;
  }
  if (trimmed.toUpperCase() === 'UTC') {
    return 'UTC';
  }
  return '';
};

const documentCategories = [
  { key: 'carrierRateConfirmation', label: 'Carrier rate confirmation' },
  { key: 'customerRateConfirmation', label: 'Customer rate confirmation' },
  { key: 'billOfLading', label: 'Bill of lading' },
  { key: 'tender', label: 'Tender' },
  { key: 'receipt', label: 'Receipt' },
  { key: 'proofOfDelivery', label: 'Proof of delivery' },
  { key: 'carrierInvoice', label: 'Carrier invoice' },
  { key: 'freightLogicInvoice', label: 'Freight Logic invoice' },
  { key: 'other', label: 'Other document' }
];

const checklistQuestions = [
  'When and where is your driver getting empty?',
  'What equipment type and length will they have?',
  'Do you have at least 2–3 load bars or straps available?',
  'What company name is on the truck and trailer?',
  'Does your driver speak English and accept tracking?',
  'If reefer, can they maintain the specified temperature?',
  'Have you confirmed driver cell and dispatcher contacts?',
  'Is the carrier aware of accessorial policy and detention?'
];

const statusOptions = [
  'Tendered',
  'Covered — Not Dispatched',
  'Dispatched — En Route',
  'At Pickup — WTL',
  'At Pickup — Loading',
  'OTR — OTW',
  'At Delivery — WTUL',
  'At Delivery — Unloading',
  'Delivered',
  'Delivered — Ready to Bill',
  'Complete',
  'On Hold'
];
const legacyStatusMap = {
  NOT_DISPATCHED: 'Tendered',
  DISPATCHED: 'Dispatched — En Route',
  DISPATCHED_NOT_LOADED: 'At Pickup — WTL',
  DISPATCHED_LOADED: 'OTR — OTW',
  OTR: 'OTR — OTW',
  UNLOADING: 'At Delivery — Unloading',
  DELIVERED: 'Delivered'
};

const normalizeStatusLabel = (value) => {
  const raw = (value || '').toString().trim();
  return legacyStatusMap[raw] || raw || 'Tendered';
};

const equipmentOptions = [
  'Dry Van',
  'Reefer',
  'Flatbed',
  'Step Deck',
  'Conestoga',
  'RGN',
  'Lowboy',
  'Hotshot Trailer',
  'Box Truck',
  'Sprinter Van',
  'Curtain Side',
  'Tanker',
  'Chassis'
];

const equipmentLengthOptions = [20, 26, 40, 45, 48, 53];

const equipmentLengthDefaults = {
  'Dry Van': 53,
  Reefer: 53,
  Flatbed: 48,
  'Step Deck': 48,
  Conestoga: 48,
  RGN: 48,
  Lowboy: 48,
  'Hotshot Trailer': 40,
  'Box Truck': 26,
  'Sprinter Van': 20,
  'Curtain Side': 48,
  Tanker: 53,
  Chassis: 40
};

const equipmentSpecialRequirementPresets = {
  'Dry Van': [
    { value: 'food_grade', label: 'Food grade' },
    { value: 'air_ride', label: 'Air ride' },
    { value: 'swing_doors', label: 'Swing doors' }
  ],
  Reefer: [
    { value: 'pre_cool', label: 'Pre-cooled' },
    { value: 'temp_recorder', label: 'Temp recorder' },
    { value: 'team_required', label: 'Team required' }
  ],
  Flatbed: [
    { value: 'tarps_required', label: 'Tarps required' },
    { value: 'chains_binders', label: 'Chains & binders' },
    { value: 'edge_protectors', label: 'Edge protectors' }
  ],
  'Step Deck': [
    { value: 'ramps_required', label: 'Ramps required' },
    { value: 'chains_binders', label: 'Chains & binders' },
    { value: 'overhang_ok', label: 'Overhang OK' }
  ],
  Conestoga: [
    { value: 'tarps_required', label: 'Tarps required' },
    { value: 'straps_required', label: 'Straps required' },
    { value: 'edge_protectors', label: 'Edge protectors' }
  ]
};

const getSpecialRequirementOptions = (type) => equipmentSpecialRequirementPresets[type] || [];

const getSpecialRequirementLabel = (type, value) => {
  const options = getSpecialRequirementOptions(type);
  return options.find((option) => option.value === value)?.label || value;
};

const formatSpecialRequirementsValue = (type, selected = []) => {
  if (!selected.length) return '';
  if (selected.length <= 2) {
    return selected.map((value) => getSpecialRequirementLabel(type, value)).join(', ');
  }
  return `${selected.length} selected`;
};

const trackingProviders = [
  { key: 'macropoint', label: 'MacroPoint' },
  { key: 'project44', label: 'Project44' },
  { key: 'eld', label: 'ELD/GPS' },
  { key: 'truckerTools', label: 'Trucker Tools' }
];

export const scheduleOptions = ['FCFS', 'Appointment'];

export const resolveStopScheduleType = (stop = {}) => {
  const appointmentType = stop.appointment_type || stop.appointmentType;
  if (appointmentType === 'Appointment' || appointmentType === 'FCFS') {
    return appointmentType;
  }
  return stop.appointment_required === true || stop.appointmentRequired === true ? 'Appointment' : 'FCFS';
};

export const isAppointmentScheduleType = (scheduleType) => scheduleType === 'Appointment';

const scheduleSegmentOptions = [
  { value: 'FCFS', label: 'FCFS' },
  { value: 'Appointment', label: 'APPT' }
];

export const ScheduleSegmentedControl = ({ value = 'FCFS', onChange, ariaLabel = 'Stop scheduling type' }) => {
  const selectedValue = resolveStopScheduleType({ appointmentType: value });

  const selectValue = (nextValue) => {
    if (nextValue !== selectedValue) {
      onChange?.(nextValue);
    }
  };

  const handleKeyDown = (event) => {
    const currentIndex = scheduleSegmentOptions.findIndex((option) => option.value === selectedValue);
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      const direction = event.key === 'ArrowRight' ? 1 : -1;
      const nextIndex = (currentIndex + direction + scheduleSegmentOptions.length) % scheduleSegmentOptions.length;
      selectValue(scheduleSegmentOptions[nextIndex].value);
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      selectValue(selectedValue);
    }
  };

  return (
    <div
      className={styles.scheduleSegmentedControl}
      role="radiogroup"
      aria-label={ariaLabel}
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      {scheduleSegmentOptions.map((option) => {
        const isSelected = option.value === selectedValue;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={isSelected}
            className={`${styles.scheduleSegment} ${isSelected ? styles.scheduleSegmentSelected : ''}`.trim()}
            data-schedule-value={option.value}
            onClick={() => selectValue(option.value)}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
};

export const StopAppointmentNumberInput = ({ value = '', onChange, onBlur, isAppointment = false }) => (
  <input
    className={`${styles.compactOverflowInput} ${!isAppointment ? styles.appointmentNumberMuted : ''}`.trim()}
    value={value}
    onChange={(event) => onChange?.(event.target.value)}
    onBlur={onBlur}
    placeholder="Appointment #"
    disabled={!isAppointment}
    aria-label="Appointment number"
  />
);

const referenceTypeOptions = [
  { value: 'PO #', label: 'PO #' },
  { value: 'SO #', label: 'SO #' },
  { value: 'Ref #', label: 'Ref #' },
  { value: 'BOL #', label: 'BOL #' },
  { value: 'Other', label: 'Other' }
];

const generateTimeOptions = () => {
  const options = [];
  for (let hour = 0; hour < 24; hour += 1) {
    for (let minute = 0; minute < 60; minute += 30) {
      const h = `${hour}`.padStart(2, '0');
      const m = `${minute}`.padStart(2, '0');
      options.push(`${h}:${m}`);
    }
  }
  return options;
};

const TIME_OPTIONS = generateTimeOptions();

const formatPhoneNumber = (value = '') => {
  const digits = (value || '').replace(/\D/g, '').slice(0, 10);
  if (!digits) return '';
  if (digits.length < 4) return `(${digits}`;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const normalizeTimeValue = (value) => {
  if (!value) {
    return '';
  }
  const trimmed = value.trim();
  if (/^\d{1,2}$/.test(trimmed)) {
    return `${trimmed.padStart(2, '0')}:00`;
  }
  if (/^\d{3,4}$/.test(trimmed)) {
    const padded = trimmed.padStart(4, '0');
    return `${padded.slice(0, 2)}:${padded.slice(2)}`;
  }
  if (/^\d{1,2}:\d{2}$/.test(trimmed)) {
    const [h, m] = trimmed.split(':');
    return `${h.padStart(2, '0')}:${m}`;
  }
  return trimmed;
};

const createStopTemplate = (type) => ({
  id: `${type}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  type,
  date: '',
  scheduleType: 'FCFS',
  needsAppointment: false,
  timeStart: '',
  timeEnd: '',
  locationId: '',
  company: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  timezone: '',
  contactPhone: '',
  contactEmail: '',
  loading: false,
  loaded: false,
  pickupNumber: '',
  appointmentNumber: '',
  notes: ''
});

const getStopStatus = (type, stop) => {
  if (type === 'pickup') {
    if (stop.loaded) return 'LOADED';
    if (stop.loading) return 'LOADING';
    return 'NOT_STARTED';
  }

  if (stop.loaded) return 'UNLOADED';
  if (stop.loading) return 'UNLOADING';
  return 'NOT_STARTED';
};

export const mapStopToEditable = (stop) => {
  if (!stop) {
    return null;
  }
  return {
    id: stop.id || `${stop.type || 'Stop'}-${Math.random().toString(36).slice(2, 8)}`,
    sequence: stop.sequence,
    type: stop.stop_type || stop.type || 'Pickup',
    date: stop.scheduledDate || stop.scheduled_date || (stop.windowStart ? moment(stop.windowStart).format('YYYY-MM-DD') : ''),
    scheduleType: resolveStopScheduleType(stop),
    needsAppointment: isAppointmentScheduleType(resolveStopScheduleType(stop)),
    timeStart: stop.windowStart ? moment(stop.windowStart).format('HH:mm') : '',
    timeEnd: stop.windowEnd ? moment(stop.windowEnd).format('HH:mm') : '',
    locationId: stop.location?.id || '',
    company: stop.location?.company_name || stop.location?.name || stop.location?.company || '',
    address: stop.location?.address || '',
    city: stop.location?.city || '',
    state: stop.location?.state || '',
    zip: stop.location?.zip || '',
    timezone: stop.timezone || stop.location?.timezone || '',
    contactPhone: stop.location?.phone || '',
    contactEmail: stop.location?.email || '',
    loading: Boolean(stop.loading),
    loaded: Boolean(stop.loaded),
    pickupNumber: stop.reference_number || stop.pickupNumber || '',
    appointmentNumber: stop.appointment_number || stop.appointmentNumber || '',
    notes: Array.isArray(stop.notes) ? stop.notes.join(' • ') : stop.notes || ''
  };
};

export const stopWindowValue = (date, time) => date && time
  ? moment(`${date} ${time}`, 'YYYY-MM-DD HH:mm').toISOString()
  : null;

export const buildStopCreatePayload = (stop, type, sequence) => ({
  stop_type: type,
  sequence,
  scheduled_date: stop.date || null,
  location_id: stop.locationId || undefined,
  window_start: stopWindowValue(stop.date, stop.timeStart),
  window_end: stopWindowValue(stop.date, stop.timeEnd),
  appointment_type: stop.scheduleType || 'FCFS',
  appointment_number: stop.appointmentNumber || '',
  reference_number: stop.pickupNumber || '',
  notes: stop.notes || ''
});

export const buildStopFieldPatch = (field, value) => ({ [field]: value });

const specialInstructionsFromStops = (stops = []) =>
  stops
    .flatMap((stop) => stop.notes || stop.instructions || [])
    .filter(Boolean);

const buildStops = (load, details) => {
  const safeLoad = load || {};
  const safeDetails = details || {};

  if (Array.isArray(safeDetails.stops) && safeDetails.stops.length) {
    return safeDetails.stops;
  }
  const stops = [];
  if (safeDetails.shipper || safeLoad.shipper) {
    stops.push({
      type: 'Pickup',
      window_start: safeDetails.pickup_window_start || safeLoad.pickup_date,
      window_end: safeDetails.pickup_window_end,
      location: safeDetails.shipper || safeLoad.shipper,
      appointment_required: safeDetails.pickup_requires_appointment,
      appointment_number: safeDetails.pickup_appointment_number,
      pickup_number: safeDetails.pickup_number,
      notes: safeDetails.pickup_notes || safeLoad.pickup_notes || []
    });
  }
  if (safeDetails.consignee || safeLoad.consignee) {
    stops.push({
      type: 'Delivery',
      window_start: safeDetails.delivery_window_start || safeLoad.delivery_date,
      window_end: safeDetails.delivery_window_end,
      location: safeDetails.consignee || safeLoad.consignee,
      appointment_required: safeDetails.delivery_requires_appointment,
      appointment_number: safeDetails.delivery_appointment_number,
      pickup_number: safeDetails.delivery_number,
      notes: safeDetails.delivery_notes || safeLoad.delivery_notes || []
    });
  }
  return stops;
};

const deriveCommodities = (details = {}) => {
  const safeDetails = details || {};
  if (Array.isArray(safeDetails.commodities) && safeDetails.commodities.length) {
    return safeDetails.commodities;
  }
  if (safeDetails.commodity) {
    return [
      {
        description: safeDetails.commodity,
        quantity: safeDetails.quantity,
        unit: safeDetails.unit,
        weight: safeDetails.weight,
        notes: safeDetails.commodity_notes
      }
    ].filter((item) => item.description || item.quantity || item.weight);
  }
  return [];
};

const deriveContacts = (details = {}) => {
  const safeDetails = details || {};
  return {
    carrier: {
      name: safeDetails.carrier_name || '',
      mc: safeDetails.carrier_mc || '',
      dot: safeDetails.carrier_dot || '',
      scac: safeDetails.carrier_scac || ''
    },
    dispatcher: {
      name: safeDetails.dispatch_name || safeDetails.dispatcher_name || '',
      phone: formatPhoneNumber(safeDetails.dispatch_phone || safeDetails.dispatcher_phone || ''),
      extension: safeDetails.dispatch_extension || safeDetails.dispatcher_extension || '',
      email: safeDetails.dispatch_email || safeDetails.dispatcher_email || ''
    },
    driver: {
      name: safeDetails.driver_name || '',
      phone: formatPhoneNumber(safeDetails.driver_phone || ''),
      truck: safeDetails.truck_number || '',
      trailer: safeDetails.trailer_number || '',
      vin: safeDetails.driver_vin || safeDetails.vin_number || ''
    }
  };
};

const deriveCosts = (details = {}, load = {}) => {
  const safeDetails = details || {};
  const safeLoad = load || {};
  const baseCost = safeDetails.costs || safeLoad.costs;
  if (Array.isArray(baseCost)) {
    return baseCost.map((cost) => ({
      id: `${cost.label || cost.description || 'cost'}-${Math.random().toString(36).slice(2, 8)}`,
      label: cost.label || cost.description || 'Line item',
      amount: cost.amount || cost.value || 0,
      quantity: cost.quantity || 1,
      paid: Boolean(cost.paid)
    }));
  }
  if (baseCost && typeof baseCost === 'object') {
    return Object.entries(baseCost).map(([label, value]) => ({
      id: `${label}-${Math.random().toString(36).slice(2, 8)}`,
      label,
      amount: value?.amount || value || 0,
      quantity: value?.quantity || 1,
      paid: Boolean(value?.paid)
    }));
  }
  if (safeLoad.bill_rate) {
    return [
      {
        id: `linehaul-${Math.random().toString(36).slice(2, 8)}`,
        label: 'Linehaul',
        amount: safeLoad.bill_rate,
        quantity: 1,
        paid: false
      }
    ];
  }
  return [];
};

const deriveEquipmentRequirements = (details = {}, load = {}) => {
  const safeDetails = details || {};
  const safeLoad = load || {};
  const payload = safeDetails.equipment_requirements || safeLoad.equipment_requirements;
  if (Array.isArray(payload) && payload.length) {
    return payload
      .map((entry) => {
        const rawLength = entry.length_ft ?? entry.length ?? entry.lengthFt ?? '';
        const parsedLength = Number(rawLength);
        const lengthValue =
          rawLength === null || rawLength === undefined || rawLength === ''
            ? ''
            : Number.isNaN(parsedLength)
              ? rawLength
              : parsedLength;
        return {
          type: entry.type || entry.equipment_type || entry.name || '',
          length_ft: lengthValue,
          special_requirements: Array.isArray(entry.special_requirements)
            ? entry.special_requirements.filter(Boolean)
            : [],
          notes: entry.notes ? String(entry.notes) : ''
        };
      })
      .filter((entry) => entry.type);
  }
  const fallbackType = safeLoad.equipment_type || safeDetails.equipment_type || '';
  const fallbackLength = safeLoad.trailer_length || safeDetails.trailer_length || '';
  if (!fallbackType) return [];
  return [
    {
      type: fallbackType,
      length_ft: fallbackLength,
      special_requirements: [],
      notes: ''
    }
  ];
};

const deriveReferences = (details = {}, load = {}) => {
  const safeDetails = details || {};
  const safeLoad = load || {};
  const payload = safeDetails.references ?? safeLoad.references ?? [];
  if (!Array.isArray(payload)) return [];
  return payload
    .map((entry, index) => {
      if (!entry || typeof entry !== 'object') return null;
      const type = String(entry.type || '').trim();
      const value = String(entry.value || '').trim();
      const label = String(entry.label || '').trim();
      if (!type && !value) return null;
      return {
        id: `reference-${Date.now()}-${index}-${Math.random().toString(36).slice(2, 6)}`,
        type: type || referenceTypeOptions[0].value,
        value,
        label
      };
    })
    .filter(Boolean);
};

const deriveCustomerCharges = (details = {}, load = {}) => {
  const safeDetails = details || {};
  const safeLoad = load || {};
  const baseCharges = safeDetails.customer_charges || safeLoad.customer_charges;
  if (Array.isArray(baseCharges) && baseCharges.length) {
    return baseCharges.map((charge) => ({
      id: `${charge.code || charge.label || 'charge'}-${Math.random().toString(36).slice(2, 8)}`,
      code: charge.code || '',
      label: charge.label || 'Charge',
      quantity: charge.quantity || 1,
      amount: charge.amount || 0
    }));
  }
  const charges = [];
  const linehaul = safeLoad.bill_rate || safeDetails.bill_rate;
  if (linehaul !== undefined && linehaul !== null && linehaul !== '') {
    charges.push({
      id: `linehaul-${Math.random().toString(36).slice(2, 8)}`,
      code: 'LINEHAUL',
      label: 'Linehaul',
      quantity: 1,
      amount: linehaul
    });
  }
  const accessorials = safeLoad.accessorial_charges || safeDetails.accessorial_charges;
  if (accessorials && typeof accessorials === 'object') {
    Object.entries(accessorials).forEach(([label, value]) => {
      charges.push({
        id: `${label}-${Math.random().toString(36).slice(2, 8)}`,
        code: '',
        label,
        quantity: value?.quantity || 1,
        amount: value?.amount || value || 0
      });
    });
  }
  if (!charges.length) {
    charges.push({
      id: `linehaul-${Math.random().toString(36).slice(2, 8)}`,
      code: 'LINEHAUL',
      label: 'Linehaul',
      quantity: 1,
      amount: 0
    });
  }
  return charges;
};

const remarksFromDetails = (details = {}) => {
  const safeDetails = details || {};
  const remarks = safeDetails.remarks || safeDetails.remark_history;
  if (Array.isArray(remarks)) {
    return remarks;
  }
  return [];
};

const pingHistoryFromDetails = (details = {}) => {
  const safeDetails = details || {};
  if (Array.isArray(safeDetails.location_history)) {
    return safeDetails.location_history;
  }
  if (Array.isArray(safeDetails.pings)) {
    return safeDetails.pings;
  }
  return [];
};

const formatCurrency = (value) => {
  const amount = Number(value || 0);
  if (Number.isNaN(amount)) {
    return '$0.00';
  }
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
};

const formatAmountDisplay = (value) => {
  const amount = Number(value || 0);
  if (!amount) {
    return '';
  }
  return formatCurrency(amount);
};

const getAmountDigits = (value) => (value || '').replace(/\D/g, '');

const getCaretPosition = (formattedValue, digitIndex) => {
  if (!formattedValue || digitIndex <= 0) {
    return formattedValue ? 1 : 0;
  }
  let count = 0;
  for (let i = 0; i < formattedValue.length; i += 1) {
    if (/\d/.test(formattedValue[i])) {
      count += 1;
      if (count === digitIndex) {
        return i + 1;
      }
    }
  }
  return formattedValue.length;
};

const decorateLineItems = (items = []) =>
  items.map((item) => ({
    ...item,
    amount: Number(item.amount || 0),
    amountDisplay: formatAmountDisplay(item.amount)
  }));

const SingleSelectDropdown = ({ value, options, placeholder, onSelect, disabled }) => {
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [menuStyles, setMenuStyles] = useState(null);
  const selectedOption = options.find((option) => option.value === value);

  const closeMenu = useCallback(() => {
    setOpen(false);
    setHighlighted(-1);
  }, []);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event) => {
      const target = event.target;
      if (triggerRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      closeMenu();
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [closeMenu, open]);

  const updateMenuPosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setMenuStyles({
      top: rect.bottom + 6,
      left: rect.left,
      width: rect.width
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    updateMenuPosition();
    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition, true);
    return () => {
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition, true);
    };
  }, [open, updateMenuPosition]);

  const handleSelect = (option) => {
    onSelect(option.value);
    closeMenu();
  };

  const handleKeyDown = (event) => {
    if (disabled) return;
    if (!options.length) return;
    if (!open && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      setOpen(true);
      setHighlighted(0);
      event.preventDefault();
      return;
    }

    if (event.key === 'ArrowDown') {
      setHighlighted((prev) => (prev + 1) % options.length);
      event.preventDefault();
    } else if (event.key === 'ArrowUp') {
      setHighlighted((prev) => (prev <= 0 ? options.length - 1 : prev - 1));
      event.preventDefault();
    } else if (event.key === 'Enter' && highlighted >= 0 && open) {
      handleSelect(options[highlighted]);
      event.preventDefault();
    } else if (event.key === 'Escape' && open) {
      closeMenu();
      event.preventDefault();
    }
  };

  return (
    <div className={styles.singleSelect}>
      <button
        type="button"
        className={styles.singleSelectTrigger}
        onClick={() => !disabled && setOpen((prev) => !prev)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        ref={triggerRef}
      >
        <span className={styles.singleSelectValue}>
          {selectedOption?.label || value || (
            <span className={styles.singleSelectPlaceholder}>{placeholder}</span>
          )}
        </span>
        <FiChevronDown aria-hidden="true" />
      </button>
      {open &&
        menuStyles &&
        createPortal(
          <div
            className={styles.singleSelectMenu}
            style={menuStyles}
            ref={menuRef}
            role="listbox"
          >
            {options.map((option, index) => {
              const isSelected = option.value === value;
              const isActive = highlighted === index || (highlighted === -1 && isSelected);
              return (
                <button
                  key={option.value}
                  type="button"
                  className={`${styles.singleSelectOption} ${isActive ? styles.singleSelectOptionActive : ''}`}
                  onMouseEnter={() => setHighlighted(index)}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => handleSelect(option)}
                >
                  {option.label}
                </button>
              );
            })}
          </div>,
          document.body
        )}
    </div>
  );
};

const LoadWorkspace = ({ load, details, onClose, isLoading, workspaceSync, workspaceView: controlledWorkspaceView, onWorkspaceViewChange }) => {
  const debouncedWrites = useDebouncedBoundaryWrites();
  const loadData = load || {};
  const detailsData = details || {};

  const structuredStops = useMemo(() => buildStops(loadData, detailsData), [loadData, detailsData]);
  const commoditiesInitial = useMemo(() => deriveCommodities(detailsData), [detailsData]);
  const contactsInitial = useMemo(() => deriveContacts(detailsData), [detailsData]);
  const costsInitial = useMemo(() => deriveCosts(detailsData, loadData), [detailsData, loadData]);
  const equipmentRequirementsInitial = useMemo(
    () => deriveEquipmentRequirements(detailsData, loadData),
    [detailsData, loadData]
  );
  const referencesInitial = useMemo(
    () => deriveReferences(detailsData, loadData),
    [detailsData, loadData]
  );
  const customerChargesInitial = useMemo(
    () => deriveCustomerCharges(detailsData, loadData),
    [detailsData, loadData]
  );
  const remarksInitial = useMemo(() => remarksFromDetails(detailsData), [detailsData]);
  const pingHistoryInitial = useMemo(() => pingHistoryFromDetails(detailsData), [detailsData]);
  const specialInstructions = useMemo(() => specialInstructionsFromStops(structuredStops), [structuredStops]);

  const handleStatusCommit = async (nextStatus) => {
    const normalized = normalizeStatusLabel(nextStatus);
    setStatus(normalized);
    if (!loadData?.id || normalized === lastSavedStatus.current) return;
    try {
      await workspaceSync.patchLoad( { status: normalized });
      lastSavedStatus.current = normalized;
    } catch (error) {
      console.error('Failed to update status', error);
    }
  };

  const currentUserName = useMemo(
    () =>
      detailsData?.current_user?.name ||
      detailsData?.current_user_name ||
      detailsData?.dispatcher_name ||
      detailsData?.current_user?.username ||
      detailsData?.current_user?.email ||
      'FreightLogic User',
    [
      detailsData?.current_user?.name,
      detailsData?.current_user_name,
      detailsData?.dispatcher_name,
      detailsData?.current_user?.username,
      detailsData?.current_user?.email
    ]
  );

  const fallbackBrowserTimeZone = useMemo(
    () => normalizeTimeZoneId(Intl.DateTimeFormat().resolvedOptions().timeZone) || 'UTC',
    []
  );

  const primaryTimeZoneId = useMemo(
    () =>
      normalizeTimeZoneId(detailsData?.timezone) ||
      normalizeTimeZoneId(loadData?.timezone) ||
      normalizeTimeZoneId(fallbackBrowserTimeZone) ||
      'UTC',
    [detailsData?.timezone, loadData?.timezone, fallbackBrowserTimeZone]
  );

  const formatDateTimeWithZone = useCallback(
    (value, timeZoneId) => {
      const dateValue = value ? new Date(value) : new Date();
      if (Number.isNaN(dateValue.getTime())) return null;

      const zone = normalizeTimeZoneId(timeZoneId) || primaryTimeZoneId || 'UTC';

      const buildParts = (targetZone) => {
        const formatter = new Intl.DateTimeFormat('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
          timeZone: targetZone,
          timeZoneName: 'short'
        });

        const parts = formatter.formatToParts(dateValue);
        const lookup = Object.fromEntries(parts.map(({ type, value: partValue }) => [type, partValue]));

        return {
          date: `${lookup.month}/${lookup.day}/${lookup.year}`,
          time: `${lookup.hour}:${lookup.minute}`,
          abbreviation: lookup.timeZoneName || '',
          timeZone: targetZone
        };
      };

      try {
        const primaryParts = buildParts(zone);
        if (primaryParts.abbreviation) return primaryParts;

        const fallbackParts = buildParts(normalizeTimeZoneId(fallbackBrowserTimeZone) || 'UTC');
        return {
          ...primaryParts,
          abbreviation: fallbackParts.abbreviation || fallbackParts.timeZone || primaryParts.timeZone
        };
      } catch (error) {
        console.error('Failed to format date with timezone', error);
        try {
          return buildParts('UTC');
        } catch (innerError) {
          console.error('UTC formatting also failed', innerError);
          return null;
        }
      }
    },
    [fallbackBrowserTimeZone, primaryTimeZoneId]
  );

  const timeZoneAbbreviation = useMemo(() => {
    const parts = formatDateTimeWithZone(new Date());
    return parts?.abbreviation || 'UTC';
  }, [formatDateTimeWithZone]);

  const deriveTimeZoneAbbreviation = useCallback(
    (value, timeZoneId) => {
      const parts = formatDateTimeWithZone(value, timeZoneId);
      return parts?.abbreviation || timeZoneAbbreviation || 'UTC';
    },
    [formatDateTimeWithZone, timeZoneAbbreviation]
  );

  const formattedStops = useMemo(
    () =>
      structuredStops.map((stop, index) => ({
        id: stop.id || `${stop.type || 'Stop'}-${index}`,
        sequence: stop.sequence,
        type: stop.stop_type || stop.type || (index === 0 ? 'Pickup' : 'Delivery'),
        location: stop.location || {},
        timezone: stop.timezone || stop.location?.timezone,
        appointmentType: stop.appointment_type,
        appointmentRequired: Boolean(stop.appointment_required),
        appointmentNumber: stop.appointment_number,
        pickupNumber: stop.reference_number || stop.pickup_number,
        scheduledDate: stop.scheduled_date,
        windowStart: stop.window_start,
        windowEnd: stop.window_end,
        notes: stop.notes
      })),
    [structuredStops]
  );

  const initialPickups = useMemo(
    () =>
      formattedStops
        .filter((stop) => (stop.type || '').toLowerCase().includes('pick'))
        .map(mapStopToEditable)
        .filter(Boolean),
    [formattedStops]
  );

  const initialDeliveries = useMemo(
    () =>
      formattedStops
        .filter((stop) => !((stop.type || '').toLowerCase().includes('pick')))
        .map(mapStopToEditable)
        .filter(Boolean),
    [formattedStops]
  );

  const initialCustomerId = useMemo(
    () =>
      [
        loadData?.customer_id,
        loadData?.customer,
        detailsData?.customer_id,
        detailsData?.customer,
      ]
        .filter(Boolean)
        .map(String)[0] || '',
    [detailsData?.customer, detailsData?.customer_id, loadData?.customer, loadData?.customer_id]
  );
  const initialCustomerName = useMemo(
    () => detailsData?.customer_name || loadData?.customer_name || '',
    [detailsData?.customer_name, loadData?.customer_name]
  );

  const [customerAssignment, setCustomerAssignment] = useState({
    id: initialCustomerId,
    name: initialCustomerName
  });
  const [customerInitialized, setCustomerInitialized] = useState(Boolean(initialCustomerId || initialCustomerName));
  const [pendingCustomer, setPendingCustomer] = useState(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerResults, setCustomerResults] = useState([]);
  const [customerHighlightedIndex, setCustomerHighlightedIndex] = useState(-1);
  const [hasSelectedCustomerOption, setHasSelectedCustomerOption] = useState(false);
  const [assigningCustomer, setAssigningCustomer] = useState(false);
  const [customerAssignError, setCustomerAssignError] = useState('');
  const [isCustomerLoading, setIsCustomerLoading] = useState(false);
  const [currentAccount, setCurrentAccount] = useState(null);
  const [canEditCustomer, setCanEditCustomer] = useState(false);
  const [isInternal, setIsInternal] = useState(false);
  const initialStatus = useMemo(
    () => normalizeStatusLabel(details?.status || load?.status || 'Tendered'),
    [details?.status, load?.status]
  );
  const [status, setStatus] = useState(initialStatus);
  const lastSavedStatus = useRef(initialStatus);
  const [billRate, setBillRate] = useState(load?.bill_rate || details?.bill_rate || '');
  const [references, setReferences] = useState(referencesInitial);
  const [selectedEquipmentTypes, setSelectedEquipmentTypes] = useState(() => {
    if (equipmentRequirementsInitial.length) {
      return equipmentRequirementsInitial.map((entry) => entry.type);
    }
    if (load?.equipment_type || details?.equipment_type) {
      return [load?.equipment_type || details?.equipment_type].filter(Boolean);
    }
    return [];
  });
  const [equipmentRequirementDetails, setEquipmentRequirementDetails] = useState(() => {
    const requirements = {};
    equipmentRequirementsInitial.forEach((entry) => {
      if (entry.type) {
        requirements[entry.type] = {
          length_ft: entry.length_ft || '',
          special_requirements: Array.isArray(entry.special_requirements)
            ? entry.special_requirements.filter(Boolean)
            : [],
          notes: entry.notes ? String(entry.notes) : ''
        };
      }
    });
    return requirements;
  });
  const [equipmentNotesEditor, setEquipmentNotesEditor] = useState(null);
  const [equipmentMenuOpen, setEquipmentMenuOpen] = useState(false);
  const equipmentTriggerRef = useRef(null);
  const equipmentMenuRef = useRef(null);
  const [equipmentMenuStyles, setEquipmentMenuStyles] = useState(null);
  const [specialRequirementsMenuType, setSpecialRequirementsMenuType] = useState(null);
  const specialRequirementsTriggerRefs = useRef({});
  const specialRequirementsMenuRef = useRef(null);
  const [specialRequirementsMenuStyles, setSpecialRequirementsMenuStyles] = useState(null);
  const [equipmentRequirementsError, setEquipmentRequirementsError] = useState('');
  const [customerBillingError, setCustomerBillingError] = useState('');
  const [customerBillingSaving, setCustomerBillingSaving] = useState(false);
  const [reeferTemperature, setReeferTemperature] = useState(
    load?.reefer_temperature || details?.reefer_temperature || details?.temperature_requirement || ''
  );
  const [reeferMode, setReeferMode] = useState(
    load?.special_load_requirements?.reefer_mode ||
      details?.special_load_requirements?.reefer_mode ||
      'Continuous'
  );
  const [reeferTempClass, setReeferTempClass] = useState(() => {
    if (load?.is_frozen || details?.is_frozen) return 'Frozen';
    if (load?.is_fresh || details?.is_fresh) return 'Fresh';
    if (load?.reefer_temperature || details?.reefer_temperature) return 'Chilled';
    return 'Fresh';
  });
  const equipmentLengthSelectOptions = useMemo(
    () => [
      { value: '', label: 'Length (ft)' },
      ...equipmentLengthOptions.map((length) => ({ value: String(length), label: `${length} ft` }))
    ],
    []
  );
  const reeferModeOptions = useMemo(
    () => [
      { value: 'Continuous', label: 'Continuous' },
      { value: 'Start-Stop', label: 'Start-Stop' }
    ],
    []
  );
  const reeferTempClassOptions = useMemo(
    () => [
      { value: 'Fresh', label: 'Fresh' },
      { value: 'Chilled', label: 'Chilled' },
      { value: 'Frozen', label: 'Frozen' }
    ],
    []
  );

  const specialRequirementsBase = useMemo(
    () => detailsData?.special_load_requirements || loadData?.special_load_requirements || {},
    [detailsData?.special_load_requirements, loadData?.special_load_requirements]
  );

  const primaryEquipmentLabel = selectedEquipmentTypes[0] || equipmentOptions[0];
  const isReeferSelected = useMemo(
    () => selectedEquipmentTypes.some((type) => type.toLowerCase().includes('reefer')),
    [selectedEquipmentTypes]
  );

  useEffect(() => {
    setStatus(initialStatus);
    lastSavedStatus.current = initialStatus;
  }, [initialStatus]);

  useEffect(() => {
    setReferences(referencesInitial);
  }, [referencesInitial]);

  useEffect(() => {
    if (!equipmentMenuOpen) return;
    const handleClickOutside = (event) => {
      const target = event.target;
      if (equipmentTriggerRef.current?.contains(target)) return;
      if (equipmentMenuRef.current?.contains(target)) return;
      setEquipmentMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [equipmentMenuOpen]);

  useEffect(() => {
    if (!specialRequirementsMenuType) return;
    const handleClickOutside = (event) => {
      const target = event.target;
      const trigger = specialRequirementsTriggerRefs.current[specialRequirementsMenuType];
      if (trigger?.contains(target)) return;
      if (specialRequirementsMenuRef.current?.contains(target)) return;
      setSpecialRequirementsMenuType(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [specialRequirementsMenuType]);

  const updateEquipmentMenuPosition = useCallback(() => {
    if (!equipmentTriggerRef.current) return;
    const rect = equipmentTriggerRef.current.getBoundingClientRect();
    setEquipmentMenuStyles({
      top: rect.bottom + 6,
      left: rect.left,
      width: rect.width
    });
  }, []);

  const updateSpecialRequirementsMenuPosition = useCallback(() => {
    if (!specialRequirementsMenuType) return;
    const trigger = specialRequirementsTriggerRefs.current[specialRequirementsMenuType];
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    setSpecialRequirementsMenuStyles({
      top: rect.bottom + 6,
      left: rect.left,
      width: rect.width
    });
  }, [specialRequirementsMenuType]);

  useLayoutEffect(() => {
    if (!equipmentMenuOpen) return;
    updateEquipmentMenuPosition();
    window.addEventListener('resize', updateEquipmentMenuPosition);
    window.addEventListener('scroll', updateEquipmentMenuPosition, true);
    return () => {
      window.removeEventListener('resize', updateEquipmentMenuPosition);
      window.removeEventListener('scroll', updateEquipmentMenuPosition, true);
    };
  }, [equipmentMenuOpen, updateEquipmentMenuPosition]);

  useLayoutEffect(() => {
    if (!specialRequirementsMenuType) return;
    updateSpecialRequirementsMenuPosition();
    window.addEventListener('resize', updateSpecialRequirementsMenuPosition);
    window.addEventListener('scroll', updateSpecialRequirementsMenuPosition, true);
    return () => {
      window.removeEventListener('resize', updateSpecialRequirementsMenuPosition);
      window.removeEventListener('scroll', updateSpecialRequirementsMenuPosition, true);
    };
  }, [specialRequirementsMenuType, updateSpecialRequirementsMenuPosition]);

  const [pickupStops, setPickupStops] = useState(() =>
    initialPickups.length > 0 ? initialPickups : [createStopTemplate('Pickup')]
  );
  const [deliveryStops, setDeliveryStops] = useState(() =>
    initialDeliveries.length > 0 ? initialDeliveries : [createStopTemplate('Delivery')]
  );

  const normalizeAccountOption = useCallback(
    (account = {}) => ({
      id: account.id ?? account.account_id ?? '',
      name: account.dba_name || account.name || account.legal_name || ''
    }),
    []
  );

  const hasCustomerSelection = useMemo(
    () => Boolean(customerAssignment.id || customerAssignment.name),
    [customerAssignment]
  );
  const shouldShowCustomerForm = useMemo(
    () => canEditCustomer && !hasCustomerSelection,
    [canEditCustomer, hasCustomerSelection]
  );

  useEffect(() => {
    let isMounted = true;

    const fetchUserAccount = async () => {
      try {
        const response = await apiClient.get(withApiPath('/users/me/'));
        if (!isMounted) return;
        const me = response.data || {};
        const account = me?.account || null;
        setCurrentAccount(account);
        const email = String(me.email || '').trim().toLowerCase();
        const isInternal = Boolean(me.is_internal) || email.endsWith('@freightlogic.io');
        // TODO: Replace with robust RBAC once available.
        setCanEditCustomer(isInternal);
        setIsInternal(isInternal);
      } catch (error) {
        console.error('Failed to load current user account', error);
      }
    };

    fetchUserAccount();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (customerInitialized) return;
    const fallbackName = initialCustomerName || currentAccount?.dba_name || '';
    const fallbackId = initialCustomerId || (currentAccount?.id ? String(currentAccount.id) : '');
    if (fallbackName || fallbackId) {
      setCustomerAssignment((prev) => ({
        id: prev.id || fallbackId,
        name: prev.name || fallbackName
      }));
      setCustomerInitialized(true);
    }
  }, [currentAccount, customerInitialized, initialCustomerId, initialCustomerName]);

  const shouldShowCustomerDropdown = useMemo(
    () =>
      customerSearch.trim() &&
      !hasSelectedCustomerOption &&
      canEditCustomer &&
      (isCustomerLoading || customerResults.length > 0),
    [canEditCustomer, customerResults.length, customerSearch, hasSelectedCustomerOption, isCustomerLoading]
  );

  useEffect(() => {
    if (!canEditCustomer) {
      setCustomerResults([]);
      return;
    }
    if (!customerSearch.trim()) {
      setCustomerResults([]);
      return;
    }
    if (hasSelectedCustomerOption) return;

    const controller = new AbortController();
    const handle = setTimeout(async () => {
      try {
        setIsCustomerLoading(true);
        const response = await apiClient.get(withApiPath('/accounts/'), {
          params: { search: customerSearch },
          signal: controller.signal
        });
        const payload = response.data?.results || response.data || [];
        setCustomerResults(Array.isArray(payload) ? payload.map(normalizeAccountOption) : []);
      } catch (error) {
        if (error.name !== 'CanceledError') {
          console.error('Customer search failed', error);
        }
        setCustomerResults([]);
      } finally {
        setIsCustomerLoading(false);
      }
    }, 250);

    return () => {
      controller.abort();
      clearTimeout(handle);
    };
  }, [canEditCustomer, customerSearch, hasSelectedCustomerOption, normalizeAccountOption]);

  useEffect(() => {
    if (shouldShowCustomerDropdown && customerResults.length > 0) {
      setCustomerHighlightedIndex((prev) => (prev >= 0 && prev < customerResults.length ? prev : 0));
    } else {
      setCustomerHighlightedIndex(-1);
    }
  }, [customerResults.length, shouldShowCustomerDropdown]);

  const handleCustomerSearchChange = (event) => {
    setCustomerSearch(event.target.value);
    setHasSelectedCustomerOption(false);
    setPendingCustomer(null);
    setCustomerAssignError('');
  };

  const handleCustomerSearchKeyDown = (event) => {
    if (!shouldShowCustomerDropdown) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setCustomerHighlightedIndex((prev) => Math.min(prev + 1, customerResults.length - 1));
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setCustomerHighlightedIndex((prev) => Math.max(prev - 1, 0));
      return;
    }

    if ((event.key === 'Enter' || event.key === 'Tab') && customerHighlightedIndex >= 0) {
      event.preventDefault();
      const customer = customerResults[customerHighlightedIndex];
      if (customer) {
        handleCustomerSelect(customer);
      }
    }
  };

  const handleCustomerSelect = (customer) => {
    const normalized = normalizeAccountOption(customer);
    setPendingCustomer(normalized);
    setCustomerSearch(normalized.name || '');
    setHasSelectedCustomerOption(true);
    setCustomerResults([]);
  };

  const handleAssignCustomer = async () => {
    if (!pendingCustomer || !canEditCustomer) return;
    const normalized = normalizeAccountOption(pendingCustomer);
    const nextCustomer = {
      id: normalized.id ? String(normalized.id) : '',
      name: normalized.name || ''
    };
    setAssigningCustomer(true);
    setCustomerAssignError('');
    try {
      if (loadData?.id) {
        await workspaceSync.patchLoad( { customer: normalized.id || null });
      }
      setCustomerAssignment(nextCustomer);
      setCustomerInitialized(true);
      setPendingCustomer(null);
      setCustomerSearch('');
      setHasSelectedCustomerOption(false);
    } catch (error) {
      console.error('Failed to assign customer', error);
      const status = error?.response?.status;
      setCustomerAssignError(
        status
          ? `Unable to assign customer. Request failed with status ${status}.`
          : 'Unable to assign customer. Please try again.'
      );
    } finally {
      setAssigningCustomer(false);
    }
  };

  const handleClearCustomer = async () => {
    if (!canEditCustomer) return;
    setAssigningCustomer(true);
    try {
      if (loadData?.id) {
        await workspaceSync.patchLoad( { customer: null });
      }
      setCustomerAssignment({ id: '', name: '' });
      setCustomerInitialized(true);
      setPendingCustomer(null);
      setCustomerSearch('');
      setHasSelectedCustomerOption(false);
    } catch (error) {
      console.error('Failed to clear customer', error);
    } finally {
      setAssigningCustomer(false);
    }
  };

  const handleCustomerInfoClick = () => {
    window.alert('Customer profile coming soon.');
  };

  const handleCarrierInfoClick = () => {
    window.alert('Carrier profile coming soon.');
  };

  const { carriers, allLocations } = useQuotesBookings();

  const normalizeCarrierOption = useCallback((carrier = {}) => ({
    id: carrier.id ?? carrier.carrier_id ?? carrier.identifier ?? carrier.mc_number ?? carrier.dot_number ?? '',
    name: carrier.name || carrier.company_name || '',
    mc: carrier.mc || carrier.mc_number || '',
    dot: carrier.dot || carrier.dot_number || '',
    scac: carrier.scac || carrier.scac_code || '',
    contacts: Array.isArray(carrier.contacts)
      ? carrier.contacts.map((contact) => ({
          id: contact.id || contact.contact_id || `${contact.name || 'contact'}-${Math.random().toString(36).slice(2, 8)}`,
          name: contact.name || '',
          role: contact.role || contact.type || '',
          phone: formatPhoneNumber(contact.phone || ''),
          extension: contact.extension || contact.ext || '',
          email: contact.email || '',
        }))
      : [],
    dispatcher: {
      name: carrier.dispatcher?.name || carrier.dispatcher_name || '',
      phone: formatPhoneNumber(carrier.dispatcher?.phone || carrier.dispatcher_phone || ''),
      extension: carrier.dispatcher?.extension || carrier.dispatcher_extension || '',
      email: carrier.dispatcher?.email || carrier.dispatcher_email || ''
    },
    driver: {
      name: carrier.driver?.name || carrier.driver_name || '',
      phone: formatPhoneNumber(carrier.driver?.phone || carrier.driver_phone || '')
    }
  }), []);

  const normalizedCarriers = useMemo(
    () => (Array.isArray(carriers) ? carriers.map(normalizeCarrierOption) : []),
    [carriers, normalizeCarrierOption]
  );

  const initialCarrierId = useMemo(
    () =>
      [
        loadData?.carrier_id,
        loadData?.carrier?.id,
        detailsData?.carrier_id,
        detailsData?.carrier?.id,
      ]
        .filter(Boolean)
        .map(String)[0] || '',
    [detailsData, loadData]
  );

  const [carrierAssignment, setCarrierAssignment] = useState({
    id: initialCarrierId,
    name: contactsInitial.carrier.name,
    mc: contactsInitial.carrier.mc,
    dot: contactsInitial.carrier.dot,
    scac: contactsInitial.carrier.scac
  });

  const [pendingCarrier, setPendingCarrier] = useState(null);
  const [assigningCarrier, setAssigningCarrier] = useState(false);

  const [dispatcher, setDispatcher] = useState({
    name: contactsInitial.dispatcher.name,
    phone: contactsInitial.dispatcher.phone,
    extension: contactsInitial.dispatcher.extension,
    email: contactsInitial.dispatcher.email
  });

  const [driver, setDriver] = useState({
    name: contactsInitial.driver.name,
    phone: contactsInitial.driver.phone,
    truck: contactsInitial.driver.truck,
    trailer: contactsInitial.driver.trailer,
    vin: contactsInitial.driver.vin
  });

  const [carrierSearch, setCarrierSearch] = useState('');
  const [carrierResults, setCarrierResults] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [hasSelectedOption, setHasSelectedOption] = useState(false);
  const [isCarrierLoading, setIsCarrierLoading] = useState(false);
  const [isAddingDispatcher, setIsAddingDispatcher] = useState(false);
  const [isAddingDriver, setIsAddingDriver] = useState(false);
  const [dispatcherDropdownOpen, setDispatcherDropdownOpen] = useState(false);
  const [driverDropdownOpen, setDriverDropdownOpen] = useState(false);

  useEffect(() => {
    if (!isAddingDispatcher) return;
    const canonical = contactsInitial.dispatcher;
    if (['name', 'phone', 'extension', 'email'].some((key) => (dispatcher[key] || '') !== (canonical[key] || ''))) {
      workspaceSync.markDirty('dispatcher', dispatcher);
    } else workspaceSync.clearDirty('dispatcher');
  // workspaceSync exposes stable registry actions for the mounted load.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactsInitial.dispatcher, dispatcher, isAddingDispatcher]);

  useEffect(() => {
    if (!isAddingDriver) return;
    const canonical = contactsInitial.driver;
    if (['name', 'phone', 'truck', 'trailer', 'vin'].some((key) => (driver[key] || '') !== (canonical[key] || ''))) {
      workspaceSync.markDirty('driver', driver);
    } else workspaceSync.clearDirty('driver');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactsInitial.driver, driver, isAddingDriver]);
  const [dispatcherErrors, setDispatcherErrors] = useState({});
  const [driverErrors, setDriverErrors] = useState({});
  const [pickupLocationSignal, setPickupLocationSignal] = useState(0);
  const [deliveryLocationSignal, setDeliveryLocationSignal] = useState(0);
  const [locationEditContext, setLocationEditContext] = useState(null);
  const [locationDrafts, setLocationDrafts] = useState({});
  const dispatcherBlurTimeout = useRef();
  const driverBlurTimeout = useRef();
  const dispatcherManualEntry = useRef(false);
  const driverManualEntry = useRef(false);
  const remarkLocationInputRef = useRef(null);
  const remarkInputRef = useRef(null);

  useEffect(() => {
    const validIds = new Set([...pickupStops, ...deliveryStops].map((stop) => stop.id));
    setLocationDrafts((prev) => {
      const next = {};
      Object.entries(prev).forEach(([key, value]) => {
        if (validIds.has(key)) {
          next[key] = value;
        }
      });
      return next;
    });
  }, [pickupStops, deliveryStops]);

  const hasCarrierSelection = useMemo(
    () =>
      Boolean(
        carrierAssignment.id ||
          carrierAssignment.name ||
          carrierAssignment.mc ||
          carrierAssignment.dot ||
          carrierAssignment.scac
      ),
    [carrierAssignment]
  );

  const selectedCarrier = useMemo(() => {
    if (carrierAssignment.id) {
      const matchById = normalizedCarriers.find(
        (carrier) => String(carrier.id) === String(carrierAssignment.id)
      );
      if (matchById) return matchById;
    }

    return (
      normalizedCarriers.find(
        (carrier) =>
          Boolean(carrier.mc && carrier.mc === carrierAssignment.mc) ||
          Boolean(carrier.dot && carrier.dot === carrierAssignment.dot) ||
          Boolean(
            carrier.name && carrierAssignment.name &&
              carrier.name.toLowerCase() === carrierAssignment.name.toLowerCase()
          )
      ) || null
    );
  }, [carrierAssignment, normalizedCarriers]);

  const selectedCarrierContacts = useMemo(
    () => (selectedCarrier && Array.isArray(selectedCarrier.contacts) ? selectedCarrier.contacts : []),
    [selectedCarrier]
  );

  useEffect(() => {
    if (!selectedCarrier) return;

    setCarrierAssignment((prev) => ({
      id: prev.id || (selectedCarrier.id ? String(selectedCarrier.id) : ''),
      name: prev.name || selectedCarrier.name || '',
      mc: prev.mc || selectedCarrier.mc || '',
      dot: prev.dot || selectedCarrier.dot || '',
      scac: prev.scac || selectedCarrier.scac || '',
    }));
  }, [selectedCarrier]);

  const hasDispatcher = useMemo(
    () => Boolean(dispatcher.name || dispatcher.phone || dispatcher.extension || dispatcher.email),
    [dispatcher]
  );

  const hasDriver = useMemo(
    () => Boolean(driver.name || driver.phone || driver.truck || driver.trailer || driver.vin),
    [driver]
  );

  const [localWorkspaceView, setLocalWorkspaceView] = useState('shipment');
  const workspaceView = controlledWorkspaceView || localWorkspaceView;
  const setWorkspaceView = useCallback((view) => {
    setLocalWorkspaceView(view);
    onWorkspaceViewChange?.(view);
  }, [onWorkspaceViewChange]);
  const [showTrackingModal, setShowTrackingModal] = useState(false);
  const [showCarrierPayModal, setShowCarrierPayModal] = useState(false);
  const [showCustomerBillModal, setShowCustomerBillModal] = useState(false);
  const [trackingProvider, setTrackingProvider] = useState(trackingProviders[0].key);

  const [trackingEntries, setTrackingEntries] = useState(
    pingHistoryInitial.map((ping) => ({
      id: `${ping.timestamp || Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      timestamp: ping.timestamp ? moment(ping.timestamp).format('YYYY-MM-DD HH:mm') : '',
      rawTimestamp: ping.timestamp ? moment(ping.timestamp).toISOString() : null,
      miles: ping.distance_to_next || ping.miles_remaining || '',
      location: ping.city ? `${ping.city}, ${ping.state}` : ping.location || '',
      notes: ping.status || '',
      author: ping.author || 'FreightLogic Bot'
    }))
  );

  const [remarks, setRemarks] = useState([]);
  const [remarksLoading, setRemarksLoading] = useState(false);

  const [remarkDraft, setRemarkDraft] = useState({
    rawTimestamp: '',
    displayDate: '',
    author: '',
    miles: '',
    location: '',
    note: '',
    isEditing: false,
    error: ''
  });

  const [commodityRows, setCommodityRows] = useState(
    commoditiesInitial.length > 0
      ? commoditiesInitial.map((commodity) => ({
          id: `${commodity.description || commodity.name || 'commodity'}-${Math.random().toString(36).slice(2, 6)}`,
          description: commodity.description || commodity.name || '',
          quantity: commodity.quantity || '',
          unit: commodity.unit || '',
          weight: commodity.weight || '',
          notes: commodity.notes || ''
        }))
      : []
  );

  const [costLineItems, setCostLineItems] = useState(
    costsInitial.length > 0
      ? decorateLineItems(costsInitial)
      : [
          {
            id: `linehaul-${Math.random().toString(36).slice(2, 6)}`,
            label: 'Linehaul',
            quantity: 1,
            amount: billRate || 0,
            amountDisplay: formatAmountDisplay(billRate),
            paid: false
          }
        ]
  );
  const [canViewCarrierFinancials, setCanViewCarrierFinancials] = useState(false);
  const [carrierPayError, setCarrierPayError] = useState('');
  useEffect(() => {
    if (!loadData.id) return;
    apiClient.get(withApiPath(`/loads/${loadData.id}/carrier-charges/`)).then(({ data }) => {
      setCanViewCarrierFinancials(true);
      setCostLineItems((data.charges || []).map(charge => ({
        id: charge.id, code: charge.charge_type, label: charge.description,
        quantity: Number(charge.quantity), amount: Number(charge.rate),
        amountDisplay: formatAmountDisplay(charge.rate), paid: false
      })));
    }).catch(error => {
      if (error.response?.status === 403) setCanViewCarrierFinancials(false);
      else setCarrierPayError('Carrier compensation could not be loaded.');
    });
  }, [loadData.id]);
  const [customerCharges, setCustomerCharges] = useState(
    customerChargesInitial.length > 0
      ? decorateLineItems(customerChargesInitial)
      : [
          {
            id: `linehaul-${Math.random().toString(36).slice(2, 6)}`,
            code: 'LINEHAUL',
            label: 'Linehaul',
            quantity: 1,
            amount: billRate || 0,
            amountDisplay: formatAmountDisplay(billRate)
          }
        ]
  );

  const totalCustomerBill = customerCharges.reduce(
    (sum, item) => sum + Number(item.amount || 0) * Number(item.quantity || 1),
    0
  );

  const [sideRailExpanded, setSideRailExpanded] = useState(false);
  const [activeUtility, setActiveUtility] = useState('leadScript');
  const [draggingStop, setDraggingStop] = useState(null);
  const [contactListModal, setContactListModal] = useState(null);
  const [showSchedulingEmailPlaceholder, setShowSchedulingEmailPlaceholder] = useState(false);
  const [timeline, setTimeline] = useState([]);
  const [timelineError, setTimelineError] = useState('');
  const [timelineLoading, setTimelineLoading] = useState(false);
  const showManualSync = shouldShowManualSync(workspaceSync);

  const openTimeline = async () => {
    setWorkspaceView('timeline');
    setTimelineLoading(true);
    setTimelineError('');
    try {
      const response = await apiClient.get(withApiPath(`/loads/${loadData.id}/changes/`), { params: { limit: 25 } });
      setTimeline(response.data?.results || []);
    } catch (error) {
      setTimelineError('Timeline is unavailable. Shipment editing is unaffected.');
    } finally {
      setTimelineLoading(false);
    }
  };

  const headerMiles = load?.miles || details?.miles;
  const milesToPickup = details?.miles_to_pickup || details?.distance_to_pickup;
  const milesToDelivery = details?.miles_to_delivery || details?.distance_to_delivery;

  const formatCityState = (stop) => {
    const cityState = [stop?.city, stop?.state].filter(Boolean).join(', ');
    return cityState || null;
  };

  const formatShortDate = (dateValue) => {
    if (!dateValue) return 'TBD';
    const parsed = moment(dateValue);
    return parsed.isValid() ? parsed.format('MM/DD') : 'TBD';
  };

  const getEarliestDate = (stops = []) => {
    const validDates = stops.map((stop) => stop?.date).filter(Boolean);
    if (!validDates.length) return null;
    return validDates.reduce((earliest, current) => {
      const currentMoment = moment(current);
      if (!currentMoment.isValid()) return earliest;
      if (!earliest) return current;
      return currentMoment.isBefore(moment(earliest)) ? current : earliest;
    }, null);
  };

  const getLatestDate = (stops = []) => {
    const validDates = stops.map((stop) => stop?.date).filter(Boolean);
    if (!validDates.length) return null;
    return validDates.reduce((latest, current) => {
      const currentMoment = moment(current);
      if (!currentMoment.isValid()) return latest;
      if (!latest) return current;
      return currentMoment.isAfter(moment(latest)) ? current : latest;
    }, null);
  };

  const laneAbbreviation = useMemo(() => {
    const pickupState = pickupStops[0]?.state || formattedStops[0]?.location?.state;
    const deliveryState =
      deliveryStops[deliveryStops.length - 1]?.state || formattedStops[formattedStops.length - 1]?.location?.state;
    if (!pickupState || !deliveryState) {
      return '—';
    }
    return `${pickupState.toUpperCase().slice(0, 2)}-${deliveryState.toUpperCase().slice(0, 2)}`;
  }, [pickupStops, deliveryStops, formattedStops]);

  const leadScript = useMemo(() => {
    const pickup = pickupStops[0];
    const drop = deliveryStops[deliveryStops.length - 1];
    const pickupWindow = pickup?.date
      ? `${moment(pickup.date).format('MMM D')} ${pickup.timeStart || ''}`.trim()
      : 'TBD';
    const dropWindow = drop?.date ? `${moment(drop.date).format('MMM D')} ${drop.timeStart || ''}`.trim() : 'TBD';
    return `We have a ${pickupStops.length + deliveryStops.length - 1} stop ${primaryEquipmentLabel} load from ${
      pickup?.city || 'origin'
    }, ${pickup?.state || ''} to ${drop?.city || 'destination'}, ${drop?.state || ''}. Pickup ${
      pickup.scheduleType === 'Appointment' ? 'by appointment' : 'FCFS'
    } ${pickupWindow}. Delivery ${drop.scheduleType === 'Appointment' ? 'by appointment' : 'FCFS'} ${dropWindow}. Freight weighs ${
      load?.weight || details?.weight || '—'
    } and bills ${totalCustomerBill ? formatCurrency(totalCustomerBill) : billRate || 'TBD'}.`;
  }, [pickupStops, deliveryStops, primaryEquipmentLabel, load, details, billRate, totalCustomerBill]);

  const primaryPickup = pickupStops[0];
  const finalDelivery = deliveryStops[deliveryStops.length - 1];
  const hasRouteEndpoints = pickupStops.length > 0 && deliveryStops.length > 0;
  const routeOrigin = formatCityState(primaryPickup) || 'TBD';
  const routeDestination = formatCityState(finalDelivery) || 'TBD';
  const pickupDateDisplay = formatShortDate(getEarliestDate(pickupStops));
  const deliveryDateDisplay = formatShortDate(getLatestDate(deliveryStops));
  const hasTimelineStops = hasRouteEndpoints;

  const getStopTimezone = useCallback(
    (stop) => deriveTimeZoneAbbreviation(stop?.date || stop?.windowStart, stop?.timezone || stop?.location?.timezone),
    [deriveTimeZoneAbbreviation]
  );

  const formatStopSchedule = useCallback(
    (stop) => {
      const baseLabel = stop?.scheduleType === 'Appointment' ? 'Appointment' : 'FCFS';
      const start = normalizeTimeValue(stop?.timeStart);
      const end = normalizeTimeValue(stop?.timeEnd);
      const tz = getStopTimezone(stop);
      const tzSegment = tz ? ` (${tz})` : '';

      if (baseLabel === 'FCFS') {
        const window = start && end ? `${start}–${end}` : '—';
        return `${baseLabel} • ${window}${tzSegment}`;
      }

      const timeDisplay = start || end || '—';
      return `${baseLabel} • ${timeDisplay}${tzSegment}`;
    },
    [getStopTimezone]
  );

  const getStopStatusVariant = useCallback((stop) => {
    // TODO: Drive node state from finalized stop events/status once available.
    if (stop?.loaded) return 'filled';
    if (stop?.loading) return 'half';
    return 'empty';
  }, []);

  const timelineStops = useMemo(() => {
    if (!hasRouteEndpoints) return [];

    const normalizeType = (type = '') => {
      const lowered = type.toLowerCase();
      if (lowered.includes('pick')) return 'pickup';
      if (lowered.includes('del')) return 'delivery';
      return 'stop';
    };

    const orderedStops = [...pickupStops, ...deliveryStops];

    return orderedStops.map((stop) => {
      const stopType = normalizeType(stop.type);

      return {
        cityState: formatCityState(stop) || 'TBD',
        type: stopType,
        scheduleDisplay: formatStopSchedule(stop),
        timezone: getStopTimezone(stop),
        statusVariant: getStopStatusVariant(stop)
      };
    });
  }, [deliveryStops, formatStopSchedule, getStopStatusVariant, getStopTimezone, hasRouteEndpoints, pickupStops]);

  const etaNodeIndex = hasTimelineStops ? 0 : -1; // TODO: Switch ETA placement to delivery once load is picked up/OTR.

  const fallbackCarrierMatches = useMemo(() => {
    const query = carrierSearch.toLowerCase();
    const carrierList = normalizedCarriers;
    if (!query) return [];
    return carrierList.filter(
      (carrier) =>
        (carrier.name || '').toLowerCase().includes(query) ||
        (carrier.mc || '').toLowerCase().includes(query) ||
        (carrier.dot || '').toLowerCase().includes(query) ||
        (carrier.scac || '').toLowerCase().includes(query)
    );
  }, [carrierSearch, normalizedCarriers]);

  const visibleCarrierResults = useMemo(() => {
    if (!carrierSearch.trim() || hasSelectedOption) return [];
    const results = carrierResults.length ? carrierResults : fallbackCarrierMatches;
    return results.slice(0, 50);
  }, [carrierResults, carrierSearch, fallbackCarrierMatches, hasSelectedOption]);

  useEffect(() => {
    if (visibleCarrierResults.length > 0) {
      setHighlightedIndex(0);
    } else {
      setHighlightedIndex(-1);
    }
  }, [visibleCarrierResults]);

  const shouldShowCarrierDropdown =
    carrierSearch.trim() && !hasSelectedOption && (isCarrierLoading || visibleCarrierResults.length > 0);

  const dispatcherOptions = useMemo(() => {
    const seen = new Set();
    const fromContacts = selectedCarrierContacts.filter((contact) => {
      const role = (contact.role || '').toLowerCase();
      if (role === 'rate confirmation delivery') return false;
      return role === 'dispatcher';
    });

    const fallback = selectedCarrier?.dispatcher?.name
      ? [
          {
            id: `${selectedCarrier.id || 'dispatcher'}-primary`,
            name: selectedCarrier.dispatcher.name,
            phone: selectedCarrier.dispatcher.phone,
            extension: selectedCarrier.dispatcher.extension,
            email: selectedCarrier.dispatcher.email,
            role: 'Dispatcher',
          },
        ]
      : [];

    return [...fromContacts, ...fallback]
      .filter((option) => option.name)
      .filter((option) => {
        const key = `${option.name.toLowerCase()}|${option.phone || ''}|${option.email || ''}|${option.extension || ''}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }, [selectedCarrier, selectedCarrierContacts]);

  const filteredDispatcherOptions = useMemo(() => {
    const query = (dispatcher.name || '').toLowerCase();
    if (!query) return dispatcherOptions;
    return dispatcherOptions.filter(
      (option) =>
        option.name.toLowerCase().includes(query) ||
        (option.phone || '').toLowerCase().includes(query) ||
        (option.email || '').toLowerCase().includes(query)
    );
  }, [dispatcher.name, dispatcherOptions]);

  const driverOptions = useMemo(() => {
    const seen = new Set();
    const fromContacts = selectedCarrierContacts.filter((contact) => {
      const role = (contact.role || '').toLowerCase();
      if (role === 'rate confirmation delivery') return false;
      return role === 'driver';
    });

    const fallback = selectedCarrier?.driver?.name
      ? [
          {
            id: `${selectedCarrier.id || 'driver'}-primary`,
            name: selectedCarrier.driver.name,
            phone: selectedCarrier.driver.phone,
            role: 'Driver',
          },
        ]
      : [];

    return [...fromContacts, ...fallback]
      .filter((option) => option.name)
      .filter((option) => {
        const key = `${option.name.toLowerCase()}|${option.phone || ''}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }, [selectedCarrier, selectedCarrierContacts]);

  const filteredDriverOptions = useMemo(() => {
    const query = (driver.name || '').toLowerCase();
    if (!query) return driverOptions;
    return driverOptions.filter(
      (option) =>
        option.name.toLowerCase().includes(query) ||
        (option.phone || '').toLowerCase().includes(query)
    );
  }, [driver.name, driverOptions]);

  useEffect(() => {
    if (!carrierSearch.trim()) {
      setCarrierResults([]);
      return;
    }

    const controller = new AbortController();
    const handle = setTimeout(async () => {
      try {
        setIsCarrierLoading(true);
        const response = await apiClient.get(withApiPath('/carriers/'), {
          params: { search: carrierSearch },
          signal: controller.signal
        });
        const payload = response.data?.results || response.data || [];
        setCarrierResults(Array.isArray(payload) ? payload.map(normalizeCarrierOption) : []);
      } catch (error) {
        if (error.name !== 'CanceledError') {
          console.error('Carrier search failed', error);
        }
        setCarrierResults([]);
      } finally {
        setIsCarrierLoading(false);
      }
    }, 250);

    return () => {
      controller.abort();
      clearTimeout(handle);
    };
  }, [carrierSearch, normalizeCarrierOption]);

  const defaultStopDate = (type) => {
    const list = type === 'pickup' ? pickupStops : deliveryStops;
    const lastDate = list[list.length - 1]?.date;
    if (lastDate) return lastDate;
    return moment().format('YYYY-MM-DD');
  };

  const currentLocation = details?.current_location || load?.current_location;

  const nextGlobalStopSequence = () => Math.max(
    0,
    ...[...pickupStops, ...deliveryStops].map((item) => Number(item.sequence) || 0)
  ) + 1;

  const consumeCanonicalStop = (stop, type, response) => {
    if (!response?.data?.id) return;
    const setStops = type === 'pickup' ? setPickupStops : setDeliveryStops;
    const canonical = mapStopToEditable({
      ...response.data,
      type: response.data.stop_type,
      windowStart: response.data.window_start,
      windowEnd: response.data.window_end
    });
    setStops((current) => current.map((item) => item.id === stop.id ? canonical : item));
  };

  const persistStopFields = (stop, type, fields) => {
    const boundary = `stop:${stop?.id}`;
    if (!stop?.locationId) {
      workspaceSync.markDirty(boundary, fields);
      return Promise.resolve(null);
    }
    const numericId = Number(stop.id);
    const request = Number.isFinite(numericId)
      ? workspaceSync.patchStop(numericId, fields)
      : workspaceSync.createStop(buildStopCreatePayload(stop, type, nextGlobalStopSequence()));
    return request
      .then((response) => {
        consumeCanonicalStop(stop, type, response);
        return response;
      });
  };

  const scheduleStopTextWrite = (stop, type, localField, apiField, value, delay = 700) => {
    const next = { ...stop, [localField]: value };
    const key = `stop:${stop.id}:${localField}`;
    debouncedWrites.schedule(key, () => persistStopFields(next, type, buildStopFieldPatch(apiField, value)), delay);
    return key;
  };

  const handleScheduleTypeChange = (type, index, value) => {
    const scheduleType = resolveStopScheduleType({ appointmentType: value });
    const updateStops = type === 'pickup' ? [...pickupStops] : [...deliveryStops];
    updateStops[index] = {
      ...updateStops[index],
      scheduleType,
      needsAppointment: isAppointmentScheduleType(scheduleType)
    };
    if (type === 'pickup') {
      setPickupStops(updateStops);
    } else {
      setDeliveryStops(updateStops);
    }
    persistStopFields(updateStops[index], type, { appointment_type: scheduleType });
  };

  const handleStopChange = (type, index, field, value, dirtyFields, normalize = false) => {
    const updateStops = type === 'pickup' ? [...pickupStops] : [...deliveryStops];
    updateStops[index] = {
      ...updateStops[index],
      [field]: normalize ? normalizeTimeValue(value) : value
    };
    workspaceSync.markDirty(`stop:${updateStops[index].id}`, dirtyFields || { [field]: value });
    if (type === 'pickup') {
      setPickupStops(updateStops);
    } else {
      setDeliveryStops(updateStops);
    }
  };

  const handleStopCheckbox = (type, index, field) => {
    const updateStops = type === 'pickup' ? [...pickupStops] : [...deliveryStops];
    updateStops[index] = {
      ...updateStops[index],
      [field]: !updateStops[index][field]
    };
    if (type === 'pickup') {
      setPickupStops(updateStops);
    } else {
      setDeliveryStops(updateStops);
    }
  };

  const handleStopStatusAdvance = (type, index) => {
    const updateStops = type === 'pickup' ? [...pickupStops] : [...deliveryStops];
    const currentStatus = getStopStatus(type, updateStops[index]);

    let nextStatus = 'NOT_STARTED';
    if (currentStatus === 'NOT_STARTED') {
      nextStatus = type === 'pickup' ? 'LOADING' : 'UNLOADING';
    } else if (currentStatus === 'LOADING' || currentStatus === 'UNLOADING') {
      nextStatus = type === 'pickup' ? 'LOADED' : 'UNLOADED';
    }

    updateStops[index] = {
      ...updateStops[index],
      loading: nextStatus === 'LOADING' || nextStatus === 'UNLOADING',
      loaded: nextStatus === 'LOADED' || nextStatus === 'UNLOADED'
    };

    if (type === 'pickup') {
      setPickupStops(updateStops);
    } else {
      setDeliveryStops(updateStops);
    }
  };

  const addStopFromLocation = (type, location) => {
    const companyName = location?.name || location?.company_name || location?.company || '';
    const baseStop = {
      id: `${type}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      type: type === 'pickup' ? 'Pickup' : 'Delivery',
      date: defaultStopDate(type),
      scheduleType: 'FCFS',
      needsAppointment: false,
      timeStart: '',
      timeEnd: '',
      locationId: location?.id || '',
      company: companyName,
      address: location?.street || location?.address || '',
      city: location?.city || '',
      state: location?.state || '',
      zip: location?.zip || '',
      contactPhone: location?.phone || '',
      contactEmail: location?.email || '',
      loading: false,
      loaded: false,
      pickupNumber: '',
      appointmentNumber: '',
      notes: location?.notes || ''
    };

    if (type === 'pickup') {
      setPickupStops([...pickupStops, baseStop]);
    } else {
      setDeliveryStops([...deliveryStops, baseStop]);
    }
  };

  const addStop = (type) => {
    const template = createStopTemplate(type === 'pickup' ? 'Pickup' : 'Delivery');
    workspaceSync.markDirty(`stop:${template.id}`, template);
    if (type === 'pickup') {
      setPickupStops([...pickupStops, template]);
    } else {
      setDeliveryStops([...deliveryStops, template]);
    }
  };

  const promptLocationSelect = (type) => {
    if (type === 'pickup') {
      setPickupLocationSignal(Date.now());
    } else {
      setDeliveryLocationSignal(Date.now());
    }
  };

  const handleLocationResolved = (type, location) => {
    if (location) {
      addStopFromLocation(type, location);
    } else {
      addStop(type);
    }
  };

  const handleStopLocationResolved = (location) => {
    if (!locationEditContext) return;
    const { type, index } = locationEditContext;
    if (!location) {
      setLocationEditContext(null);
      return;
    }

    const updateStops = type === 'pickup' ? [...pickupStops] : [...deliveryStops];
    updateStops[index] = {
      ...updateStops[index],
      locationId: location.id || updateStops[index].locationId,
      company: location.name || location.company_name || updateStops[index].company,
      address: location.street || location.address || updateStops[index].address,
      city: location.city || updateStops[index].city,
      state: location.state || updateStops[index].state,
      zip: location.zip || updateStops[index].zip,
      contactPhone: location.phone || updateStops[index].contactPhone,
      contactEmail: location.email || updateStops[index].contactEmail
    };

    if (type === 'pickup') {
      setPickupStops(updateStops);
    } else {
      setDeliveryStops(updateStops);
    }
    setLocationEditContext(null);
  };

  const handleCarrierSearchChange = (event) => {
    setCarrierSearch(event.target.value);
    setHasSelectedOption(false);
  };

  const handleCarrierSearchKeyDown = (event) => {
    if (!visibleCarrierResults.length) return;

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightedIndex((prev) => {
        if (!visibleCarrierResults.length) return -1;
        if (event.key === 'ArrowDown') {
          if (prev < 0) return 0;
          return (prev + 1) % visibleCarrierResults.length;
        }
        if (prev <= 0) return visibleCarrierResults.length - 1;
        return prev - 1;
      });
      return;
    }

    if ((event.key === 'Enter' || event.key === 'Tab') && highlightedIndex >= 0) {
      event.preventDefault();
      const carrier = visibleCarrierResults[highlightedIndex];
      if (carrier) {
        handleCarrierSelect(carrier);
      }
    }
  };

  const handleCarrierSelect = (carrier) => {
    const normalized = normalizeCarrierOption(carrier);
    setPendingCarrier(normalized);
    setCarrierSearch(normalized.name || '');
    setHasSelectedOption(true);
    setHighlightedIndex(-1);
  };

  const handleAssignCarrier = async () => {
    if (!pendingCarrier) return;
    const normalized = normalizeCarrierOption(pendingCarrier);
    const nextCarrier = {
      id: normalized.id ? String(normalized.id) : '',
      name: normalized.name || '',
      mc: normalized.mc || '',
      dot: normalized.dot || '',
      scac: normalized.scac || ''
    };
    setAssigningCarrier(true);
    try {
      if (loadData?.id && normalized?.id) {
        await workspaceSync.patchLoad( { carrier_id: normalized.id });
      }
      setCarrierAssignment(nextCarrier);
      setPendingCarrier(null);
      setCarrierSearch('');
      setHasSelectedOption(false);
    } catch (error) {
      console.error('Failed to assign carrier', error);
    } finally {
      setAssigningCarrier(false);
    }
  };

  const handleClearCarrier = async () => {
    setAssigningCarrier(true);
    try {
      if (loadData?.id) {
        await workspaceSync.patchLoad( { carrier_id: null });
      }
      setCarrierAssignment({ id: '', name: '', mc: '', dot: '', scac: '' });
      setPendingCarrier(null);
      setCarrierSearch('');
      setDispatcher({ name: '', phone: '', extension: '', email: '' });
      setDriver({ name: '', phone: '', truck: '', trailer: '', vin: '' });
      setDispatcherErrors({});
      setDriverErrors({});
      setIsAddingDispatcher(false);
      setIsAddingDriver(false);
      setHasSelectedOption(false);
    } catch (error) {
      console.error('Failed to clear carrier', error);
    } finally {
      setAssigningCarrier(false);
    }
  };

  const applyDispatcherContact = useCallback((contact) => {
    if (!contact) return;
    dispatcherManualEntry.current = false;
    setDispatcher((prev) => ({
      ...prev,
      role: contact.role || 'Dispatcher',
      name: contact.name || prev.name,
      phone: formatPhoneNumber(contact.phone || prev.phone),
      extension: contact.extension || prev.extension,
      email: contact.email || prev.email
    }));
    setDispatcherErrors((prev) => {
      const next = { ...prev };
      delete next.name;
      delete next.phone;
      delete next.email;
      return next;
    });
    setDispatcherDropdownOpen(false);
  }, []);

  const applyDriverContact = useCallback((contact) => {
    if (!contact) return;
    driverManualEntry.current = false;
    setDriver((prev) => ({
      ...prev,
      role: contact.role || 'Driver',
      name: contact.name || prev.name,
      phone: formatPhoneNumber(contact.phone || prev.phone)
    }));
    setDriverErrors((prev) => {
      const next = { ...prev };
      delete next.name;
      delete next.phone;
      return next;
    });
    setDriverDropdownOpen(false);
  }, []);

  useEffect(() => {
    if (!isAddingDispatcher || !dispatcher.name) return;
    if (dispatcherManualEntry.current) return;
    const exact = dispatcherOptions.find(
      (option) => option.name.toLowerCase() === dispatcher.name.toLowerCase()
    );
    if (
      exact &&
      (dispatcher.phone !== exact.phone || dispatcher.email !== exact.email || dispatcher.extension !== exact.extension)
    ) {
      applyDispatcherContact(exact);
    }
  }, [
    applyDispatcherContact,
    dispatcher.email,
    dispatcher.extension,
    dispatcher.name,
    dispatcher.phone,
    dispatcherOptions,
    isAddingDispatcher
  ]);

  useEffect(() => {
    if (!isAddingDriver || !driver.name) return;
    if (driverManualEntry.current) return;
    const exact = driverOptions.find((option) => option.name.toLowerCase() === driver.name.toLowerCase());
    if (exact && driver.phone !== exact.phone) {
      applyDriverContact(exact);
    }
  }, [applyDriverContact, driver.name, driver.phone, driverOptions, isAddingDriver]);

  useEffect(
    () => () => {
      clearTimeout(dispatcherBlurTimeout.current);
      clearTimeout(driverBlurTimeout.current);
    },
    []
  );

  const clearDispatcherFieldError = useCallback((field) => {
    setDispatcherErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const clearDriverFieldError = useCallback((field) => {
    setDriverErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const handleDispatcherPhoneChange = (value) => {
    workspaceSync.markDirty('dispatcher');
    setDispatcher((prev) => ({ ...prev, phone: formatPhoneNumber(value) }));
  };

  const handleDispatcherPhoneBlur = () => {
    setDispatcher((prev) => ({ ...prev, phone: formatPhoneNumber(prev.phone) }));
  };

  const handleDriverPhoneChange = (value) => {
    workspaceSync.markDirty('driver');
    setDriver((prev) => ({ ...prev, phone: formatPhoneNumber(value) }));
  };

  const handleDriverPhoneBlur = () => {
    setDriver((prev) => ({ ...prev, phone: formatPhoneNumber(prev.phone) }));
  };

  const validateDispatcher = () => {
    const errors = {};
    if (!(dispatcher.name || '').trim()) errors.name = 'Dispatcher name is required.';
    if (!(dispatcher.phone || '').trim()) errors.phone = 'Dispatcher phone is required.';
    if (!(dispatcher.email || '').trim()) errors.email = 'Dispatcher email is required.';
    setDispatcherErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateDriver = () => {
    const errors = {};
    if (!(driver.name || '').trim()) errors.name = 'Driver name is required.';
    if (!(driver.phone || '').trim()) errors.phone = 'Driver mobile is required.';
    if (!(driver.truck || '').trim()) errors.truck = 'Truck number is required.';
    setDriverErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleDispatcherSave = async () => {
    if (!validateDispatcher()) return;

    const sanitizedDispatcher = {
      name: (dispatcher.name || '').trim(),
      phone: formatPhoneNumber(dispatcher.phone),
      extension: (dispatcher.extension || '').trim() || null,
      email: (dispatcher.email || '').trim(),
    };

    try {
      await workspaceSync.patchLoad({
        dispatch_name: sanitizedDispatcher.name,
        dispatch_phone: sanitizedDispatcher.phone,
        dispatch_extension: sanitizedDispatcher.extension,
        dispatch_email: sanitizedDispatcher.email
      });
      setDispatcher(sanitizedDispatcher);
      setIsAddingDispatcher(false);
    } catch (_) {
      // The shared save indicator owns retry/error feedback and the draft remains open.
    }
  };

  const handleDriverSave = async () => {
    if (!validateDriver()) return;

    const sanitizedDriver = {
      name: (driver.name || '').trim(),
      phone: formatPhoneNumber(driver.phone),
      truck: (driver.truck || '').trim(),
      trailer: (driver.trailer || '').trim() || null,
      vin: (driver.vin || '').trim() || null,
    };

    try {
      await workspaceSync.patchLoad({
        driver_name: sanitizedDriver.name,
        driver_phone: sanitizedDriver.phone,
        truck_number: sanitizedDriver.truck,
        trailer_number: sanitizedDriver.trailer,
        driver_vin: sanitizedDriver.vin
      });
      setDriver(sanitizedDriver);
      setIsAddingDriver(false);
    } catch (_) {
      // The shared save indicator owns retry/error feedback and the draft remains open.
    }
  };

  const handleStopDragStart = (type, index) => {
    setDraggingStop({ type, index });
  };

  const handleStopDrop = (type, index) => {
    if (!draggingStop || draggingStop.type !== type) {
      setDraggingStop(null);
      return;
    }
    const list = type === 'pickup' ? [...pickupStops] : [...deliveryStops];
    const [moved] = list.splice(draggingStop.index, 1);
    list.splice(index, 0, moved);
    const previous = type === 'pickup' ? pickupStops : deliveryStops;
    if (type === 'pickup') {
      setPickupStops(list);
    } else {
      setDeliveryStops(list);
    }
    setDraggingStop(null);
    const allStops = type === 'pickup' ? [...list, ...deliveryStops] : [...pickupStops, ...list];
    const orderedIds = allStops.map((stop) => Number(stop.id));
    if (orderedIds.every(Number.isFinite)) {
      workspaceSync.markDirty('stopOrder', orderedIds);
      workspaceSync.reorderStops(orderedIds).then((response) => {
        const canonical = (response.data?.stops || []).map((stop) => mapStopToEditable({
          ...stop, windowStart: stop.window_start, windowEnd: stop.window_end
        }));
        setPickupStops(canonical.filter((stop) => stop.type === 'pickup'));
        setDeliveryStops(canonical.filter((stop) => stop.type === 'delivery'));
      }).catch(() => {
        if (type === 'pickup') setPickupStops(previous); else setDeliveryStops(previous);
      });
    }
  };

  const saveFailureText = (() => {
    const diagnostic = workspaceSync.failureDiagnostic || workspaceSync.conflictDiagnostic;
    if (!diagnostic) return '';
    const boundary = diagnostic.boundary || '';
    let context = 'Shipment';
    if (boundary.startsWith('stop:')) {
      const stopId = boundary.split(':')[1];
      const stop = [...pickupStops, ...deliveryStops].find((item) => String(item.id) === stopId);
      context = stop?.type?.toLowerCase().includes('pick') ? 'Pickup stop' : stop ? 'Delivery stop' : 'Stop';
    } else if (boundary === 'dispatcher') context = 'Dispatcher';
    else if (boundary === 'customerBilling') context = 'Billing';
    const detail = Object.entries(diagnostic.details || {})[0];
    const suffix = detail ? ` · ${detail[0]}: ${detail[1]}` : diagnostic.currentRevision ? ` · Server revision ${diagnostic.currentRevision}` : '';
    return `${context} save ${diagnostic.status === 409 ? 'conflict' : 'failed'}${diagnostic.status ? ` · ${diagnostic.status}` : ''}${suffix}`;
  })();

  const formatLocationName = (stop = {}) => {
    const name = stop.company || stop.name || stop.company_name || '';
    const stateCode = (stop.state || '').trim();
    if (!name) return '';
    return stateCode ? `${name} (${stateCode.toUpperCase()})` : name;
  };

  const buildLocationTooltip = (stop = {}) => {
    const name = stop.company || '—';
    const addressLine = stop.address || '';
    const cityStateZip = [[stop.city, stop.state].filter(Boolean).join(', '), stop.zip]
      .filter(Boolean)
      .join(' ');
    return [name, addressLine, cityStateZip].filter(Boolean).join('\n');
  };

  const openLocationProfile = (locationId) => {
    if (!locationId) return;
    window.open(`/locations/${locationId}`, '_blank', 'noopener');
  };

  const addTrackingEntry = () => {
    setTrackingEntries([
      ...trackingEntries,
      {
        id: `tracking-${Date.now()}`,
        timestamp: moment().format('YYYY-MM-DD HH:mm'),
        rawTimestamp: moment().toISOString(),
        miles: '',
        location: '',
        notes: ''
      }
    ]);
  };

  const updateTrackingEntry = (index, field, value) => {
    const entries = [...trackingEntries];
    entries[index] = {
      ...entries[index],
      [field]: value,
      ...(field === 'timestamp' ? { rawTimestamp: moment(value).toISOString() } : {})
    };
    setTrackingEntries(entries);
  };

  const [isAddingRemark, setIsAddingRemark] = useState(false);
  const formatActivityTimestamp = useCallback(
    (rawTimestamp, fallback) => {
      const primaryParts = formatDateTimeWithZone(rawTimestamp);
      if (primaryParts) {
        return `${primaryParts.date} · ${primaryParts.time} (${primaryParts.abbreviation || timeZoneAbbreviation})`;
      }

      const fallbackParts = formatDateTimeWithZone(fallback);
      if (fallbackParts) {
        return `${fallbackParts.date} · ${fallbackParts.time} (${fallbackParts.abbreviation || timeZoneAbbreviation})`;
      }

      return '—';
    },
    [formatDateTimeWithZone, timeZoneAbbreviation]
  );

  const sortRemarks = useCallback((items = []) => {
    const deriveSortKey = (remark) => {
      const raw = remark?.rawTimestamp || remark?.timestamp;
      return raw && moment(raw).isValid() ? moment(raw).valueOf() : 0;
    };

    return items.slice().sort((a, b) => deriveSortKey(b) - deriveSortKey(a));
  }, []);

  const normalizeRemark = useCallback(
    (remark) => ({
      id: remark.id || remark.public_id || `remark-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      author: remark.author_name || remark.author || currentUserName,
      timestamp: remark.created_at || remark.timestamp || remark.rawTimestamp || moment().toISOString(),
      rawTimestamp: remark.created_at || remark.rawTimestamp || remark.timestamp || null,
      note: remark.remark || remark.note || remark.text || remark.message || '',
      location: remark.location_text || remark.location || '',
      miles: typeof remark.miles === 'number' ? remark.miles : remark.miles || '',
    }),
    [currentUserName]
  );

  const orderedRemarks = useMemo(() => sortRemarks(remarks), [remarks, sortRemarks]);

  const draftDateDisplay = remarkDraft.rawTimestamp
    ? formatActivityTimestamp(remarkDraft.rawTimestamp)
    : remarkDraft.displayDate
    ? `${remarkDraft.displayDate} · —:— (${timeZoneAbbreviation})`
    : '—';

  const resetRemarkDraft = useCallback(
    () =>
      setRemarkDraft({
        rawTimestamp: '',
        displayDate: '',
        author: '',
        miles: '',
        location: '',
        note: '',
        isEditing: false,
        error: ''
      }),
    []
  );

  useEffect(() => {
    let isActive = true;

    const fetchRemarks = async () => {
      if (!loadData?.id) {
        if (isActive) {
          setRemarks(sortRemarks(remarksInitial.map((remark) => normalizeRemark(remark))));
        }
        return;
      }

      setRemarksLoading(true);
      try {
        const response = await apiClient.get(withApiPath(`/loads/${loadData.id}/remarks/`));
        if (!isActive) return;
        const data = Array.isArray(response?.data) ? response.data : [];
        setRemarks(sortRemarks(data.map((remark) => normalizeRemark(remark))));
      } catch (error) {
        console.error('Failed to load remarks', error);
        if (isActive) {
          setRemarks(sortRemarks(remarksInitial.map((remark) => normalizeRemark(remark))));
        }
      } finally {
        if (isActive) {
          setRemarksLoading(false);
        }
      }
    };

    fetchRemarks();
    resetRemarkDraft();

    return () => {
      isActive = false;
    };
  }, [loadData?.id, normalizeRemark, remarksInitial, resetRemarkDraft, sortRemarks]);

  const startRemarkDraftEditing = useCallback(
    (focusField = 'remark') => {
      setRemarkDraft((prev) => {
        const nextDate = moment().format('MM/DD/YYYY');
        return {
          ...prev,
          rawTimestamp: moment().toISOString(),
          displayDate: nextDate,
          author: currentUserName,
          isEditing: true,
          error: ''
        };
      });

      requestAnimationFrame(() => {
        if (focusField === 'remark') {
          remarkInputRef.current?.focus();
        } else {
          remarkLocationInputRef.current?.focus();
        }
      });
    },
    [currentUserName]
  );

  const handleRemarkDraftChange = (field, value) => {
    setRemarkDraft((prev) => ({ ...prev, [field]: value, error: field === 'note' ? '' : prev.error }));
  };

  const addRemark = async () => {
    const trimmedRemark = (remarkDraft.note || '').trim();
    const draftTimestamp = remarkDraft.rawTimestamp || moment().toISOString();

    if (!remarkDraft.isEditing) {
      startRemarkDraftEditing();
      return;
    }

    if (!trimmedRemark || isAddingRemark) {
      setRemarkDraft((prev) => ({ ...prev, error: trimmedRemark ? prev.error : 'Remark is required' }));
      return;
    }

    setIsAddingRemark(true);

    try {
      let createdRemark = null;

      if (loadData?.id) {
        const response = await workspaceSync.createRemark({
          remark: trimmedRemark,
          location_text: remarkDraft.location || null,
        });
        createdRemark = normalizeRemark(response?.data || {});
      }

      if (!createdRemark) {
        createdRemark = normalizeRemark({
          id: `remark-${Date.now()}`,
          author_name: remarkDraft.author || currentUserName,
          created_at: draftTimestamp,
          remark: trimmedRemark,
          location_text: remarkDraft.location || '',
        });
      }

      setRemarks((prev) => sortRemarks([createdRemark, ...prev]));
      resetRemarkDraft();
    } catch (error) {
      console.error('Failed to add remark', error);
    } finally {
      setIsAddingRemark(false);
    }
  };

  const cancelRemarkDraft = useCallback(() => {
    setIsAddingRemark(false);
    resetRemarkDraft();
  }, [resetRemarkDraft]);

  const addCommodity = () => {
    setCommodityRows([
      ...commodityRows,
      {
        id: `commodity-${Date.now()}`,
        description: '',
        quantity: '',
        unit: '',
        weight: '',
        notes: ''
      }
    ]);
  };

  const updateCommodity = (index, field, value) => {
    const items = [...commodityRows];
    items[index] = { ...items[index], [field]: value };
    setCommodityRows(items);
  };

  const removeCommodity = (index) => {
    const items = [...commodityRows];
    items.splice(index, 1);
    setCommodityRows(items);
  };

  const addCostLineItem = () => {
    setCostLineItems([
      ...costLineItems,
      {
        id: `cost-${Date.now()}`,
        label: 'Accessorial',
        quantity: 1,
        amount: 0,
        amountDisplay: '',
        paid: false
      }
    ]);
  };

  const updateCostLineItem = (index, field, value) => {
    const items = [...costLineItems];
    const parsedValue = field === 'quantity' ? Number(value) : value;
    items[index] = { ...items[index], [field]: parsedValue };
    setCostLineItems(items);
  };

  const handleLineItemAmountChange = (setItems) => (index, event) => {
    const input = event.target;
    const rawValue = input.value;
    const digits = getAmountDigits(rawValue);
    const caretDigits = getAmountDigits(rawValue.slice(0, input.selectionStart || 0)).length;
    const displayValue = digits ? formatCurrency(Number(digits)) : '';
    setItems((prev) => {
      const next = [...prev];
      next[index] = {
        ...next[index],
        amount: digits ? Number(digits) : 0,
        amountDisplay: displayValue
      };
      return next;
    });
    const caretPosition = getCaretPosition(displayValue, caretDigits);
    window.requestAnimationFrame(() => {
      if (document.activeElement === input) {
        input.setSelectionRange(caretPosition, caretPosition);
      }
    });
  };

  const handleLineItemAmountBlur = (setItems) => (index) => {
    setItems((prev) => {
      const next = [...prev];
      const amountValue = Number(next[index].amount || 0);
      next[index] = {
        ...next[index],
        amount: amountValue,
        amountDisplay: amountValue ? formatCurrency(amountValue) : ''
      };
      return next;
    });
  };

  const toggleCostPaid = (index) => {
    const items = [...costLineItems];
    items[index] = { ...items[index], paid: !items[index].paid };
    setCostLineItems(items);
  };

  const removeCostLineItem = (index) => {
    const items = [...costLineItems];
    items.splice(index, 1);
    setCostLineItems(items);
  };

  const getDefaultEquipmentLength = useCallback(
    (type) => {
      if (equipmentLengthDefaults[type]) return equipmentLengthDefaults[type];
      const fallback = loadData?.trailer_length || detailsData?.trailer_length;
      return fallback ? Number(fallback) : '';
    },
    [detailsData?.trailer_length, loadData?.trailer_length]
  );

  const commitEquipmentRequirements = useCallback(
    async (nextTypes, nextRequirements, { propagateError = false } = {}) => {
      if (!loadData?.id || !isInternal) return;
      workspaceSync.markDirty('equipment', { types: nextTypes, requirements: nextRequirements });
      if (!nextTypes.length) {
        setEquipmentRequirementsError('');
        try {
          await workspaceSync.patchLoad( {
            equipment_requirements: [],
            equipment_type: null,
            trailer_length: null
          });
        } catch (error) {
          console.error('Failed to update equipment requirements', error);
          if (propagateError) throw error;
        }
        return;
      }
      const missing = nextTypes.filter((type) => !nextRequirements[type]?.length_ft);
      if (missing.length) {
        setEquipmentRequirementsError('Select a length for every equipment type.');
        return;
      }
      setEquipmentRequirementsError('');
      const payload = nextTypes.map((type) => {
        const entry = nextRequirements[type] || {};
        const lengthValue = entry.length_ft ?? '';
        const parsedLength = Number(lengthValue);
        return {
          type,
          length_ft:
            lengthValue === '' || lengthValue === null || lengthValue === undefined
              ? lengthValue
              : Number.isNaN(parsedLength)
                ? lengthValue
                : parsedLength,
          special_requirements: Array.isArray(entry.special_requirements)
            ? entry.special_requirements
            : [],
          notes: entry.notes ?? ''
        };
      });
      const primaryLength = nextRequirements[nextTypes[0]]?.length_ft;
      try {
        await workspaceSync.patchLoad( {
          equipment_requirements: payload,
          equipment_type: nextTypes[0] || null,
          trailer_length: primaryLength ? Number(primaryLength) : null
        });
      } catch (error) {
        console.error('Failed to update equipment requirements', error);
        if (propagateError) throw error;
      }
    },
    [isInternal, loadData?.id]
  );

  const toggleEquipmentType = (type) => {
    if (!isInternal) return;
    setSelectedEquipmentTypes((prev) => {
      const exists = prev.includes(type);
      const nextTypes = exists ? prev.filter((item) => item !== type) : [...prev, type];
      setEquipmentRequirementDetails((current) => {
        const nextRequirements = { ...current };
        if (exists) {
          delete nextRequirements[type];
          if (equipmentNotesEditor === type) setEquipmentNotesEditor(null);
          if (specialRequirementsMenuType === type) {
            setSpecialRequirementsMenuType(null);
          }
        } else if (!nextRequirements[type]) {
          nextRequirements[type] = {
            length_ft: getDefaultEquipmentLength(type),
            special_requirements: [],
            notes: ''
          };
        }
        commitEquipmentRequirements(nextTypes, nextRequirements);
        return nextRequirements;
      });
      return nextTypes;
    });
  };

  const updateEquipmentLength = (type, value) => {
    if (!isInternal) return;
    const lengthValue = value ? Number(value) : '';
    setEquipmentRequirementDetails((prev) => {
      const nextRequirements = {
        ...prev,
        [type]: {
          ...(prev[type] || {}),
          length_ft: lengthValue
        }
      };
      commitEquipmentRequirements(selectedEquipmentTypes, nextRequirements);
      return nextRequirements;
    });
  };

  const updateEquipmentSpecialRequirements = (type, value) => {
    if (!isInternal) return;
    setEquipmentRequirementDetails((prev) => {
      const nextRequirements = {
        ...prev,
        [type]: {
          ...(prev[type] || {}),
          special_requirements: value
        }
      };
      commitEquipmentRequirements(selectedEquipmentTypes, nextRequirements);
      return nextRequirements;
    });
  };

  const saveEquipmentNotes = async (type, value) => {
    const nextRequirements = {
      ...equipmentRequirementDetails,
      [type]: { ...(equipmentRequirementDetails[type] || {}), notes: value }
    };
    await commitEquipmentRequirements(selectedEquipmentTypes, nextRequirements, { propagateError: true });
    setEquipmentRequirementDetails(nextRequirements);
    setEquipmentNotesEditor(null);
  };

  const normalizeReferencePayload = useCallback((items = []) => {
    if (!Array.isArray(items)) return [];
    return items
      .map((item) => {
        const type = (item.type || '').toString().trim();
        const value = (item.value || '').toString().trim();
        const label = (item.label || '').toString().trim();
        if (!type || !value) return null;
        const entry = { type, value };
        if (label) {
          entry.label = label;
        }
        return entry;
      })
      .filter(Boolean);
  }, []);

  const commitReferences = useCallback(
    async (nextReferences) => {
      if (!loadData?.id) return;
      const payload = normalizeReferencePayload(nextReferences);
      try {
        await workspaceSync.patchLoad( { references: payload });
      } catch (error) {
        console.error('Failed to update references', error);
      }
    },
    [loadData?.id, normalizeReferencePayload]
  );

  const addReference = () => {
    setReferences((prev) => {
      const next = [...prev, {
        id: `reference-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        type: referenceTypeOptions[0].value,
        value: '',
        label: ''
      }];
      workspaceSync.markDirty('references', normalizeReferencePayload(next));
      return next;
    });
  };

  const updateReference = (index, updates, { commit } = {}) => {
    setReferences((prev) => {
      const next = [...prev];
      const current = next[index] || {};
      const nextItem = { ...current, ...updates };
      if (updates.type && updates.type !== 'Other') {
        nextItem.label = '';
      }
      next[index] = nextItem;
      workspaceSync.markDirty('references', normalizeReferencePayload(next));
      if (commit) {
        commitReferences(next);
      }
      return next;
    });
  };

  const removeReference = (index) => {
    setReferences((prev) => {
      const next = prev.filter((_, itemIndex) => itemIndex !== index);
      commitReferences(next);
      return next;
    });
  };

  const addCustomerCharge = () => {
    if (!isInternal) return;
    workspaceSync.markDirty('customerBilling');
    setCustomerCharges((prev) => [
      ...prev,
      {
        id: `charge-${Date.now()}`,
        code: '',
        label: 'Accessorial',
        quantity: 1,
        amount: 0,
        amountDisplay: ''
      }
    ]);
  };

  const updateCustomerCharge = (index, field, value) => {
    if (!isInternal) return;
    workspaceSync.markDirty('customerBilling');
    setCustomerCharges((prev) => {
      const next = [...prev];
      const parsedValue = field === 'quantity' ? Number(value) : value;
      next[index] = { ...next[index], [field]: parsedValue };
      return next;
    });
  };

  const removeCustomerCharge = (index) => {
    if (!isInternal) return;
    workspaceSync.markDirty('customerBilling');
    setCustomerCharges((prev) => {
      const next = [...prev];
      next.splice(index, 1);
      return next.length ? next : prev;
    });
  };

  const commitCustomerCharges = useCallback(
    async (nextCharges) => {
      if (!loadData?.id || !isInternal) return;
      const normalized = nextCharges.map((charge) => ({
        code: charge.code || null,
        label: charge.label || 'Charge',
        quantity: Number(charge.quantity || 1),
        amount: Number(charge.amount || 0)
      }));
      const linehaul = normalized.find((charge) => (charge.label || '').toLowerCase() === 'linehaul');
      return workspaceSync.patchLoad({
        customer_charges: normalized,
        bill_rate: linehaul ? linehaul.amount : billRate || null
      }, 'customerBilling');
    },
    [billRate, isInternal, loadData?.id]
  );

  const commitReeferSettings = useCallback(
    async (nextMode, nextTemp, nextClass) => {
      if (!loadData?.id || !isInternal) return;
      const nextSpecial = {
        ...(specialRequirementsBase || {}),
        reefer_mode: nextMode,
        temp_class: nextClass
      };
      const payload = {
        reefer_temperature: nextTemp || null,
        is_fresh: nextClass === 'Fresh',
        is_frozen: nextClass === 'Frozen',
        special_load_requirements: nextSpecial
      };
      try {
        await workspaceSync.patchLoad( payload);
      } catch (error) {
        console.error('Failed to update reefer requirements', error);
      }
    },
    [isInternal, loadData?.id, specialRequirementsBase]
  );

  const scheduleReeferSettings = useCallback((nextMode, nextTemp, nextClass, immediate = false) => {
    workspaceSync.markDirty('reefer', {
      reefer_temperature: nextTemp || null, is_fresh: nextClass === 'Fresh', is_frozen: nextClass === 'Frozen',
      special_load_requirements: { ...(specialRequirementsBase || {}), reefer_mode: nextMode, temp_class: nextClass }
    });
    const write = () => commitReeferSettings(nextMode, nextTemp, nextClass);
    debouncedWrites.schedule('reefer', write, 700);
    if (immediate) return debouncedWrites.flush('reefer');
    return undefined;
  }, [commitReeferSettings, debouncedWrites, workspaceSync]);

  useEffect(() => workspaceSync.registerFlusher(debouncedWrites.flushAll), [debouncedWrites.flushAll, workspaceSync]);

  useEffect(() => {
    const linehaul = customerCharges.find((charge) => (charge.label || '').toLowerCase() === 'linehaul');
    if (!linehaul) return;
    const normalized = Number(linehaul.amount || 0);
    if (Number(normalized) === Number(billRate || 0)) return;
    setBillRate(normalized);
  }, [billRate, customerCharges]);

  const totalCarrierPay = costLineItems.reduce(
    (sum, item) => sum + Number(item.amount || 0) * Number(item.quantity || 1),
    0
  );

  const driverTrackingPrompt = driver.phone
    ? `Send ${trackingProviders.find((provider) => provider.key === trackingProvider)?.label} invite to ${driver.phone}`
    : 'Enter driver phone to trigger tracking invite.';

  const getAppointmentEmails = (location = {}) => {
    const contacts = Array.isArray(location.contacts) ? location.contacts : [];
    const roleMatches = ['appointment', 'schedule', 'scheduling'];
    const contactEmails = contacts
      .filter((contact) => {
        const role = (contact.role || '').toLowerCase();
        return roleMatches.some((matcher) => role.includes(matcher));
      })
      .map((contact) => contact.email)
      .filter(Boolean);

    const uniqueEmails = Array.from(new Set(contactEmails.length ? contactEmails : [location.email].filter(Boolean)));
    return uniqueEmails;
  };

  const getDockPhones = (location = {}) => {
    const contacts = Array.isArray(location.contacts) ? location.contacts : [];
    const roleMatches = ['dock', 'gate', 'yard', 'guard'];
    const contactPhones = contacts
      .filter((contact) => {
        const role = (contact.role || '').toLowerCase();
        return roleMatches.some((matcher) => role.includes(matcher));
      })
      .map((contact) => [contact.phone, contact.ext].filter(Boolean).join(contact.ext ? ' ext ' : ''))
      .filter(Boolean);

    const fallback = location.phone ? [location.phone] : [];
    return contactPhones.length ? contactPhones : fallback;
  };

  const hasStopLocation = (stop) =>
    Boolean(stop.company || stop.address || stop.city || stop.state || stop.zip || stop.location?.id);

  const resolveStopTimezoneLabel = (stopList) => {
    const stopWithLocation = (stopList || []).find((stop) => hasStopLocation(stop) || stop.timezone);
    if (!stopWithLocation) return deriveTimeZoneAbbreviation(new Date(), primaryTimeZoneId);

    const timezoneSource = stopWithLocation.timezone || stopWithLocation.location?.timezone || primaryTimeZoneId;
    return deriveTimeZoneAbbreviation(stopWithLocation.date || stopWithLocation.windowStart, timezoneSource);
  };

  const handleRemoveStop = (type, index) => {
    const updateStops = type === 'pickup' ? [...pickupStops] : [...deliveryStops];
    const removed = updateStops[index];
    if (Number.isFinite(Number(removed?.id))) {
      // Keep the row visible until DELETE is acknowledged.
      workspaceSync.deleteStop(Number(removed.id)).then(() => {
        updateStops.splice(index, 1);
        const next = updateStops.length ? updateStops : [createStopTemplate(type === 'pickup' ? 'Pickup' : 'Delivery')];
        if (type === 'pickup') setPickupStops(next); else setDeliveryStops(next);
      }).catch(() => {});
      return;
    }
    updateStops.splice(index, 1);
    if (type === 'pickup') {
      setPickupStops(updateStops.length ? updateStops : [createStopTemplate('Pickup')]);
    } else {
      setDeliveryStops(updateStops.length ? updateStops : [createStopTemplate('Delivery')]);
    }
  };

  const renderStopTable = (type, stops) => {
    const timezoneLabel = resolveStopTimezoneLabel(stops);
    const windowStartLabel = timezoneLabel ? `Window start (${timezoneLabel})` : 'Window start';
    const windowEndLabel = timezoneLabel ? `Window end (${timezoneLabel})` : 'Window end';
    const locationOptionId = `${type}-location-options`;

    const handleLocationClear = (stopIndex) => {
      const updateStops = type === 'pickup' ? [...pickupStops] : [...deliveryStops];
      const stopId = updateStops[stopIndex]?.id;

      updateStops[stopIndex] = {
        ...updateStops[stopIndex],
        locationId: '',
        company: '',
        address: '',
        city: '',
        state: '',
        zip: '',
        timezone: ''
      };

      setLocationDrafts((prev) => ({
        ...prev,
        ...(stopId ? { [stopId]: '' } : {})
      }));

      if (type === 'pickup') {
        setPickupStops(updateStops);
      } else {
        setDeliveryStops(updateStops);
      }
      workspaceSync.markDirty(`stop:${stopId}`, { location_id: null });
    };

    const handleLocationApply = (stopIndex, location) => {
      if (!location) return;
      const updateStops = type === 'pickup' ? [...pickupStops] : [...deliveryStops];
      const targetStop = updateStops[stopIndex];

      updateStops[stopIndex] = {
        ...targetStop,
        locationId: location.id || targetStop?.locationId,
        company: location.name || location.company_name || targetStop?.company,
        address: location.street || location.address || targetStop?.address,
        city: location.city || targetStop?.city,
        state: location.state || targetStop?.state,
        zip: location.zip || targetStop?.zip,
        timezone: location.timezone || targetStop?.timezone,
        contactPhone: location.phone || targetStop?.contactPhone,
        contactEmail: location.email || targetStop?.contactEmail
      };

      setLocationDrafts((prev) => {
        const next = { ...prev };
        if (targetStop?.id) {
          delete next[targetStop.id];
        }
        return next;
      });

      if (type === 'pickup') {
        setPickupStops(updateStops);
      } else {
        setDeliveryStops(updateStops);
      }
      persistStopFields(updateStops[stopIndex], type, { location_id: updateStops[stopIndex].locationId }).catch(() => {});
    };

    const handleLocationCommit = (stopIndex, value) => {
      const trimmed = value.trim();
      const draftStop = stops[stopIndex];
      const stopId = draftStop?.id;

      if (!trimmed) {
        handleLocationClear(stopIndex);
        return;
      }

      const matchedLocation = allLocations.find(
        (location) => formatLocationName(location).toLowerCase() === trimmed.toLowerCase()
      );

      if (matchedLocation) {
        handleLocationApply(stopIndex, matchedLocation);
        return;
      }

      if (stopId) {
        setLocationDrafts((prev) => {
          const next = { ...prev };
          delete next[stopId];
          return next;
        });
      }
    };

    const handleLocationInputChange = (stopIndex, value) => {
      const draftStop = stops[stopIndex];
      if (draftStop?.id) {
        setLocationDrafts((prev) => ({ ...prev, [draftStop.id]: value }));
      }
      if (!value.trim()) {
        handleLocationClear(stopIndex);
      }
    };

    const getLocationValue = (stop) => {
      if (stop?.id && Object.prototype.hasOwnProperty.call(locationDrafts, stop.id)) {
        return locationDrafts[stop.id];
      }
      return formatLocationName(stop);
    };

    return (
      <div className={styles.tableWrapper}>
        <table className={`${styles.gridTable} ${styles.stopTable}`}>
          <thead>
            <tr>
              <th className={styles.controlHeader} aria-label="Remove" />
              <th className={styles.controlHeader} aria-label="Reorder" />
              <th className={styles.dateColumn}>Date</th>
              <th className={styles.scheduleTypeColumn}>Schedule</th>
              <th className={styles.timeColumn}>{windowStartLabel}</th>
              <th className={styles.timeColumn}>{windowEndLabel}</th>
              <th className={styles.locationColumn}>Location</th>
              <th className={styles.numberColumn}>{type === 'pickup' ? 'Pickup #' : 'Delivery #'}</th>
              <th className={styles.numberColumn}>Appointment #</th>
              <th className={styles.notesColumn}>Route notes</th>
              <th className={styles.statusColumn}>Status</th>
            </tr>
          </thead>
          <tbody>
            {stops.map((stop, index) => (
              <tr
                key={stop.id}
                draggable
                onDragStart={() => handleStopDragStart(type, index)}
                onDragOver={(event) => event.preventDefault()}
                onDrop={() => handleStopDrop(type, index)}
                className={draggingStop?.type === type && draggingStop.index === index ? styles.dragRow : ''}
              >
                <td className={styles.deleteCell}>
                  <IconButton
                    size="sm"
                    variant="ghost"
                    onClick={() => handleRemoveStop(type, index)}
                    title="Remove stop"
                  >
                    <FiX />
                  </IconButton>
                </td>
                <td className={styles.dragHandle} title="Drag to reorder">≡</td>
                <td className={styles.dateColumn}>
                  <input
                    type="date"
                    value={stop.date}
                    onChange={(event) => handleStopChange(type, index, 'date', event.target.value, { scheduled_date: event.target.value || null })}
                    onBlur={(event) => persistStopFields({ ...stop, date: event.target.value }, type, { scheduled_date: event.target.value || null })}
                  />
                </td>
                <td className={styles.scheduleTypeColumn}>
                  <ScheduleSegmentedControl
                    value={stop.scheduleType}
                    onChange={(value) => handleScheduleTypeChange(type, index, value)}
                    ariaLabel={`${type} stop ${index + 1} scheduling type`}
                  />
                </td>
                <td className={styles.timeColumn}>
                  <input
                    list="time-options"
                    value={stop.timeStart}
                    onChange={(event) => handleStopChange(type, index, 'timeStart', event.target.value, { window_start: stopWindowValue(stop.date, event.target.value) })}
                    onBlur={(event) => {
                      const value = normalizeTimeValue(event.target.value);
                      handleStopChange(type, index, 'timeStart', value, { window_start: stopWindowValue(stop.date, value) });
                      persistStopFields({ ...stop, timeStart: value }, type, { window_start: stopWindowValue(stop.date, value) });
                    }}
                    placeholder="HH:MM"
                  />
                </td>
                <td className={styles.timeColumn}>
                  <input
                    list="time-options"
                    value={stop.timeEnd}
                    onChange={(event) => handleStopChange(type, index, 'timeEnd', event.target.value, { window_end: stopWindowValue(stop.date, event.target.value) })}
                    onBlur={(event) => {
                      const value = normalizeTimeValue(event.target.value);
                      handleStopChange(type, index, 'timeEnd', value, { window_end: stopWindowValue(stop.date, value) });
                      persistStopFields({ ...stop, timeEnd: value }, type, { window_end: stopWindowValue(stop.date, value) });
                    }}
                    placeholder="HH:MM"
                  />
                </td>

                <td className={styles.locationColumn}>
                  <div className={styles.locationCell} title={buildLocationTooltip(stop)}>
                    <div className={styles.locationField}>
                      <input
                        className={`${styles.locationInput} ${getLocationValue(stop) ? styles.locationInputFilled : ''}`.trim()}
                        value={getLocationValue(stop)}
                        list={locationOptionId}
                        onDoubleClick={() => setLocationEditContext({ type, index, signal: Date.now() })}
                        onChange={(event) => handleLocationInputChange(index, event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            event.preventDefault();
                            handleLocationCommit(index, event.target.value);
                          }
                        }}
                        onBlur={(event) => handleLocationCommit(index, event.target.value)}
                        placeholder="Add location"
                        title={buildLocationTooltip(stop)}
                      />
                      <button
                        type="button"
                        className={styles.locationInfoButton}
                        onClick={() => openLocationProfile(stop.locationId)}
                        disabled={!stop.locationId}
                        aria-label="Open location profile"
                        title={buildLocationTooltip(stop)}
                      >
                        <FiInfo />
                      </button>
                    </div>
                    {!stop.locationId && Number.isFinite(Number(stop.id)) && (
                      <p className={styles.errorText}>Select a replacement location or remove this stop.</p>
                    )}
                  </div>
                </td>
                <td className={styles.numberColumn}>
                  <input
                    className={styles.compactOverflowInput}
                    value={stop.pickupNumber}
                    onChange={(event) => { handleStopChange(type, index, 'pickupNumber', event.target.value, { reference_number: event.target.value }); scheduleStopTextWrite(stop, type, 'pickupNumber', 'reference_number', event.target.value); }}
                    onBlur={() => debouncedWrites.flush(`stop:${stop.id}:pickupNumber`)}
                    placeholder={type === 'pickup' ? 'Pickup #' : 'Delivery #'}
                  />
                </td>
                <td className={styles.numberColumn}>
                  <StopAppointmentNumberInput
                    value={stop.appointmentNumber}
                    onChange={(value) => { handleStopChange(type, index, 'appointmentNumber', value, { appointment_number: value }); scheduleStopTextWrite(stop, type, 'appointmentNumber', 'appointment_number', value); }}
                    onBlur={() => debouncedWrites.flush(`stop:${stop.id}:appointmentNumber`)}
                    isAppointment={isAppointmentScheduleType(stop.scheduleType)}
                  />
                </td>
                <td className={styles.notesColumn}>
                  <input
                    value={stop.notes}
                    onChange={(event) => { handleStopChange(type, index, 'notes', event.target.value, { notes: event.target.value }); scheduleStopTextWrite(stop, type, 'notes', 'notes', event.target.value, 900); }}
                    onBlur={() => debouncedWrites.flush(`stop:${stop.id}:notes`)}
                    placeholder="Route notes"
                  />
                </td>
                <td className={styles.statusColumn}>
                  <button
                    type="button"
                    className={`${styles.statusPill} ${styles[`status${getStopStatus(type, stop)}`]}`}
                    onClick={() => handleStopStatusAdvance(type, index)}
                    title="Status will sync with driver events in a future update"
                  >
                    {(() => {
                      const status = getStopStatus(type, stop);
                      if (status === 'LOADING') return 'Loading';
                      if (status === 'LOADED') return 'Loaded';
                      if (status === 'UNLOADING') return 'Unloading';
                      if (status === 'UNLOADED') return 'Unloaded';
                      return 'Status';
                    })()}
                  </button>
                  {/* TODO: Tie NOT_STARTED → LOADING to driver on-site events (OS-WTL),
                    LOADING → LOADED to driver-loaded events (OTR-OTW), and allow
                    API-driven status updates without breaking this UI cycle. */}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <datalist id={locationOptionId}>
          {allLocations.map((location) => {
            const optionLabel = formatLocationName(location);
            if (!optionLabel) return null;

            return (
              <option
                key={location.id}
                value={optionLabel}
                label={[location.city, location.state, location.zip].filter(Boolean).join(', ')}
              />
            );
          })}
        </datalist>
      </div>
    );
  };

  const renderTrackingModal = () => (
    <div className={styles.modalBackdrop}>
      <div className={styles.modal} role="dialog" aria-modal="true">
        <header className={styles.modalHeader}>
          <h3>Send tracking invite</h3>
          <Button variant="ghost" leadingIcon={<FiX />} onClick={() => setShowTrackingModal(false)}>
            Close
          </Button>
        </header>
        <div className={styles.modalBodyVertical}>
          <label className={styles.fieldLabel}>
            Tracking provider
            <select value={trackingProvider} onChange={(event) => setTrackingProvider(event.target.value)}>
              {trackingProviders.map((provider) => (
                <option key={provider.key} value={provider.key}>
                  {provider.label}
                </option>
              ))}
            </select>
          </label>
          <label className={styles.fieldLabel}>
            Driver phone
            <input
              value={driver.phone}
              onChange={(event) => setDriver({ ...driver, phone: event.target.value })}
              placeholder="Enter mobile number"
            />
          </label>
          <div className={styles.modalHint}>{driverTrackingPrompt}</div>
          <Button size="lg" leadingIcon={<FiSend />} onClick={() => setShowTrackingModal(false)}>
            Send tracking link
          </Button>
        </div>
      </div>
    </div>
  );

  const renderLineItemModal = ({
    title,
    items,
    includePaid,
    onClose,
    onAdd,
    onUpdate,
    onRemove,
    onTogglePaid,
    onSave,
    saveLabel,
    error,
    saving,
    onRetry,
    onAmountChange,
    onAmountBlur
  }) => (
    <div className={styles.modalBackdrop}>
      <div className={styles.modal} role="dialog" aria-modal="true">
        <header className={styles.modalHeader}>
          <h3>{title}</h3>
          <Button variant="ghost" leadingIcon={<FiX />} onClick={onClose}>
            Close
          </Button>
        </header>
        <div className={styles.modalBodyVertical}>
          <div className={`${styles.tableWrapper} ${styles.modalTableWrapper}`}>
            <table className={`${styles.gridTable} ${styles.lineItemTable}`}>
              <thead>
                <tr>
                  <th className={styles.lineItemLabelColumn}>Line item</th>
                  <th className={styles.lineItemQtyColumn}>Qty</th>
                  <th className={styles.lineItemAmountColumn}>Amount</th>
                  {includePaid && <th className={styles.lineItemPaidColumn}>Paid</th>}
                  <th className={styles.lineItemActionsColumn} />
                </tr>
              </thead>
              <tbody>
                {items.map((item, index) => (
                  <tr key={item.id}>
                    <td className={styles.lineItemLabelColumn}>
                      <input
                        value={item.label}
                        onChange={(event) => onUpdate(index, 'label', event.target.value)}
                      />
                    </td>
                    <td className={styles.lineItemQtyColumn}>
                      <input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(event) => onUpdate(index, 'quantity', event.target.value)}
                      />
                    </td>
                    <td className={styles.lineItemAmountColumn}>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={item.amountDisplay || ''}
                        onChange={(event) => onAmountChange(index, event)}
                        onBlur={() => onAmountBlur(index)}
                      />
                    </td>
                    {includePaid && (
                      <td className={`${styles.checkboxCell} ${styles.lineItemPaidColumn}`}>
                        <input type="checkbox" checked={item.paid} onChange={() => onTogglePaid(index)} />
                      </td>
                    )}
                    <td className={`${styles.actionsCell} ${styles.lineItemActionsColumn}`}>
                      <IconButton size="sm" onClick={() => onRemove(index)}>
                        <FiTrash2 />
                      </IconButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className={styles.modalActions}>
            {error && <p className={styles.errorText} role="alert">{error}</p>}
            {error && onRetry && <Button variant="ghost" size="sm" onClick={onRetry} disabled={saving}>Retry</Button>}
            <Button variant="ghost" size="sm" leadingIcon={<FiPlus />} onClick={onAdd}>
              Add line item
            </Button>
            <Button size="lg" onClick={onSave} disabled={saving}>
              {saving ? 'Saving…' : saveLabel}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  const handleCarrierPaySave = useCallback(async () => {
    setCarrierPayError('');
    try {
      const response = await apiClient.put(withApiPath(`/loads/${loadData.id}/carrier-charges/`), {
        base_load_revision: workspaceSync.revision,
        charges: costLineItems.map((item, index) => ({ sequence: index + 1,
          charge_type: item.code || (index === 0 ? 'LINEHAUL' : 'ACCESSORIAL'), description: item.label,
          quantity: item.quantity || 1, rate: item.amount || 0,
          amount: Number(item.quantity || 1) * Number(item.amount || 0), currency: 'USD' }))
      });
      setCostLineItems(response.data.charges.map(charge => ({ id: charge.id, code: charge.charge_type,
        label: charge.description, quantity: Number(charge.quantity), amount: Number(charge.rate),
        amountDisplay: formatAmountDisplay(charge.rate), paid: false })));
      await workspaceSync.refresh();
      setShowCarrierPayModal(false);
    } catch (error) {
      setCarrierPayError(error.response?.status === 409 ? 'Shipment changed. Sync before saving carrier pay.' : 'Carrier pay could not be saved.');
    }
  }, [costLineItems, loadData.id, workspaceSync]);

  const renderCarrierPayModal = () =>
    renderLineItemModal({
      title: 'Edit carrier pay',
      items: costLineItems,
      includePaid: true,
      onClose: () => setShowCarrierPayModal(false),
      onAdd: addCostLineItem,
      onUpdate: updateCostLineItem,
      onRemove: removeCostLineItem,
      onTogglePaid: toggleCostPaid,
      onSave: handleCarrierPaySave,
      saveLabel: 'Save carrier pay',
      error: carrierPayError,
      onAmountChange: handleLineItemAmountChange(setCostLineItems),
      onAmountBlur: handleLineItemAmountBlur(setCostLineItems)
    });

  const handleCustomerBillSave = useCallback(async () => {
    setCustomerBillingSaving(true);
    setCustomerBillingError('');
    try {
      await commitCustomerCharges(customerCharges);
      setShowCustomerBillModal(false);
    } catch (_) {
      setCustomerBillingError('Customer billing could not be saved. Retry when ready.');
    } finally {
      setCustomerBillingSaving(false);
    }
  }, [commitCustomerCharges, customerCharges]);

  const renderCustomerBillModal = () =>
    renderLineItemModal({
      title: 'Edit customer bill',
      items: customerCharges,
      includePaid: false,
      onClose: () => setShowCustomerBillModal(false),
      onAdd: addCustomerCharge,
      onUpdate: updateCustomerCharge,
      onRemove: removeCustomerCharge,
      onTogglePaid: () => {},
      onSave: handleCustomerBillSave,
      saveLabel: 'Save customer bill',
      error: customerBillingError,
      saving: customerBillingSaving,
      onRetry: handleCustomerBillSave,
      onAmountChange: handleLineItemAmountChange(setCustomerCharges),
      onAmountBlur: handleLineItemAmountBlur(setCustomerCharges)
    });

  const renderUtilityPanel = () => {
    if (!sideRailExpanded) {
      return null;
    }

    if (activeUtility === 'leadScript') {
      return <pre className={styles.utilityContent}>{leadScript}</pre>;
    }

    if (activeUtility === 'checklist') {
      return (
        <ul className={styles.utilityList}>
          {checklistQuestions.map((question) => (
            <li key={question}>
              <label>
                <input type="checkbox" />
                <span>{question}</span>
              </label>
            </li>
          ))}
        </ul>
      );
    }

    if (activeUtility === 'messaging') {
      return (
        <div className={styles.utilityMessaging}>
          <textarea rows={5} placeholder="Message driver or dispatcher…" />
          <Button size="sm" leadingIcon={<FiSend />}>Send SMS</Button>
        </div>
      );
    }

    return <div className={styles.utilityPlaceholder}>Select a tool to begin.</div>;
  };

  return (
    <div className={styles.backdrop}>
      <div className={`${styles.workspace} ${workspaceView !== 'shipment' ? styles.workspaceBounded : ''} ${sideRailExpanded ? styles.workspaceRailExpanded : ''}`} role="dialog" aria-modal="true">
        <LocationSelector
          label="Pickup location"
          value={null}
          onChange={(location) => handleLocationResolved('pickup', location)}
          renderTrigger={false}
          openSignal={pickupLocationSignal}
        />
        <LocationSelector
          label="Delivery location"
          value={null}
          onChange={(location) => handleLocationResolved('delivery', location)}
          renderTrigger={false}
          openSignal={deliveryLocationSignal}
        />
        {locationEditContext && (
          <LocationSelector
            label="Edit stop location"
            value={null}
            onChange={handleStopLocationResolved}
            renderTrigger={false}
            openSignal={locationEditContext.signal}
          />
        )}
        <header className={styles.header}>
          <div>
            <span className={styles.subheading}>Load administration</span>
            <h2>
              {load?.public_load_id || load?.load_id || load?.id || 'Unassigned'} · {laneAbbreviation}
            </h2>
            <div className={styles.metaRow}>
              <span>Status: {status}</span>
              <span>BOL: {getReferenceValue(load, 'bol') || '—'}</span>
              <span>PO: {getReferenceValue(load, 'po') || '—'}</span>
              {load?.reference && <span>Ref: {load.reference}</span>}
              {headerMiles && <span>{headerMiles} mi</span>}
              {milesToPickup && <span>{milesToPickup} mi to pickup</span>}
              {milesToDelivery && <span>{milesToDelivery} mi to delivery</span>}
            </div>
          </div>
          <div className={styles.workspaceControls}>
            <span className={`${styles.saveState} ${workspaceSync.conflict || workspaceSync.failure ? styles.saveStateAlert : ''}`}>
              {workspaceSync.saveState}
            </span>
            {workspaceSync.failure && <button type="button" onClick={workspaceSync.retryFailed}>{workspaceSync.failedBoundaries.length > 1 ? 'RETRY ALL' : 'RETRY'}</button>}
            {workspaceSync.conflict && workspaceSync.conflictBoundaries.length === 1 && <button type="button" onClick={() => workspaceSync.resolveConflict(workspaceSync.conflictBoundaries[0])}>REFRESH &amp; RETRY</button>}
            <button type="button" className={styles.refreshControl} onClick={workspaceSync.refresh} aria-label="Refresh shipment">↻</button>
            {workspaceSync.viewerCount !== null && <span>{workspaceSync.viewerCount} {workspaceSync.viewerCount === 1 ? 'VIEWER' : 'VIEWERS'}</span>}
            {showManualSync && <><span>· {workspaceSync.unseenChanges} {workspaceSync.unseenChanges === 1 ? 'CHANGE' : 'CHANGES'}</span><button type="button" onClick={workspaceSync.refresh}>{workspaceSync.conflict ? 'REVIEW' : 'SYNC'}</button></>}
            <button type="button" className={styles.timelineControl} onClick={openTimeline}>Timeline</button>
            <Button variant="ghost" onClick={onClose} leadingIcon={<FiX />}>Close</Button>
          </div>
          {saveFailureText && <div className={styles.errorText} role="alert">{saveFailureText}</div>}
        </header>

        {isLoading && <div className={styles.loadingOverlay}>Syncing real-time load data…</div>}

        <main className={styles.workspaceMain}>
        <div className={styles.activeView}>
        {workspaceView === 'timeline' && <ShipmentTimeline timeline={timeline} loading={timelineLoading} error={timelineError} onReturn={() => setWorkspaceView('shipment')} />}
        {workspaceView === 'documents' && loadData.id && <DocumentsConsole loadId={loadData.id} onClose={() => setWorkspaceView('shipment')} />}
        {workspaceView === 'shipment' && <>
        <div className={styles.topRow}>
          <Surface padding="xl" className={styles.overviewCard}>
            <div className={styles.overviewHeader}>
              <div className={styles.customerSection}>
                <span className={styles.overviewLabel}>Customer</span>
                {shouldShowCustomerForm ? (
                  <div className={styles.customerSearchPanel}>
                    <div className={styles.customerSearchRow}>
                      <input
                        value={customerSearch}
                        onChange={handleCustomerSearchChange}
                        onKeyDown={handleCustomerSearchKeyDown}
                        autoComplete="off"
                        autoCorrect="off"
                        spellCheck={false}
                        placeholder="Search by DBA name"
                      />
                      {pendingCustomer && (
                        <Button size="sm" onClick={handleAssignCustomer} disabled={assigningCustomer}>
                          {assigningCustomer ? 'Adding…' : 'Add customer'}
                        </Button>
                      )}
                    </div>
                    {customerAssignError && <p className={styles.errorText}>{customerAssignError}</p>}
                    {customerSearch.trim() && !hasSelectedCustomerOption ? (
                      shouldShowCustomerDropdown ? (
                        <div className={styles.customerDropdown}>
                          {isCustomerLoading && <p className={styles.assignmentHint}>Searching customers…</p>}
                          {!isCustomerLoading &&
                            customerResults.map((customer, index) => (
                              <button
                                key={customer.id || `${customer.name || 'customer'}-${index}`}
                                type="button"
                                className={`${styles.carrierResult} ${
                                  pendingCustomer?.id === customer.id || customerHighlightedIndex === index
                                    ? styles.carrierResultActive
                                    : ''
                                }`}
                                onClick={() => handleCustomerSelect(customer)}
                              >
                                <div>
                                  <p className={styles.carrierName}>{customer.name || 'Customer'}</p>
                                </div>
                              </button>
                            ))}
                        </div>
                      ) : (
                        !isCustomerLoading && <p className={styles.assignmentHint}>No customers found.</p>
                      )
                    ) : (
                      <p className={styles.assignmentHint}>
                        {pendingCustomer
                          ? 'Click “Add customer” to confirm this selection.'
                          : 'Start typing to search customers.'}
                      </p>
                    )}
                  </div>
                ) : (
                  <div className={styles.carrierPreview}>
                    <div className={styles.carrierPreviewMain}>
                      <div>
                        <p className={styles.carrierName}>{customerAssignment.name || 'Customer not assigned'}</p>
                        <p className={styles.carrierMeta}>Customer Account · Active · Verified</p>
                      </div>
                      <div className={styles.customerActions}>
                        {hasCustomerSelection && (
                          <button
                            type="button"
                            className={styles.infoIconButton}
                            onClick={handleCustomerInfoClick}
                            title="Customer profile"
                          >
                            <FiInfo />
                          </button>
                        )}
                        {hasCustomerSelection && (
                          <IconButton
                            size="sm"
                            variant="ghost"
                            onClick={handleClearCustomer}
                            title="Clear customer"
                            disabled={!canEditCustomer || assigningCustomer}
                            className={styles.previewIconButton}
                          >
                            <FiX />
                          </IconButton>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <IconButton size="md" onClick={() => setWorkspaceView('documents')} title="Manage documents">
                <FiFileText />
              </IconButton>
            </div>
            <div className={styles.customerModules}>
              <div className={styles.customerDivider} />
              <div className={styles.customerModule}>
                <span className={styles.sectionTitle}>Operations</span>
                <div className={styles.customerFieldStack}>
                  <label className={styles.fieldLabelStack}>
                    Status
                    <TimeDropdownInput
                      value={status}
                      onChange={setStatus}
                      onSelect={handleStatusCommit}
                      onBlur={handleStatusCommit}
                      options={statusOptions}
                      placeholder="Select status"
                      normalizeInput={(value) => value}
                      normalizeSelection={(value) => value}
                      readOnly
                    />
                  </label>
                  <label className={styles.fieldLabelStack}>
                    Lane
                    <input value={laneAbbreviation} readOnly />
                  </label>
                  <div className={styles.referencesBlock}>
                    <div className={styles.referencesHeader}>
                      <span className={styles.fieldLabelText}>References</span>
                      <Button size="sm" variant="ghost" leadingIcon={<FiPlus />} onClick={addReference}>
                        Add reference
                      </Button>
                    </div>
                    {references.length ? (
                      <div className={styles.referencesList}>
                        {references.map((reference, index) => (
                          <div
                            key={reference.id}
                            className={`${styles.referenceRow} ${
                              reference.type === 'Other' ? styles.referenceRowOther : ''
                            }`}
                          >
                            <SingleSelectDropdown
                              value={reference.type}
                              options={referenceTypeOptions}
                              placeholder="Type"
                              onSelect={(nextType) => {
                                const hasValue = Boolean((reference.value || '').trim());
                                updateReference(index, { type: nextType }, { commit: hasValue });
                              }}
                            />
                            {reference.type === 'Other' && (
                              <input
                                className={styles.referenceLabelInput}
                                placeholder="Label"
                                value={reference.label}
                                onChange={(event) =>
                                  updateReference(index, { label: event.target.value })
                                }
                                onBlur={(event) =>
                                  updateReference(index, { label: event.target.value }, { commit: true })
                                }
                              />
                            )}
                            <input
                              className={styles.referenceValueInput}
                              placeholder="Value"
                              value={reference.value}
                              onChange={(event) =>
                                updateReference(index, { value: event.target.value })
                              }
                              onBlur={(event) =>
                                updateReference(index, { value: event.target.value }, { commit: true })
                              }
                              onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                  updateReference(index, { value: event.target.value }, { commit: true });
                                  if (index === references.length - 1) {
                                    addReference();
                                  }
                                }
                              }}
                            />
                            <IconButton
                              size="sm"
                              variant="ghost"
                              onClick={() => removeReference(index)}
                              title="Remove reference"
                              className={styles.referenceRemoveButton}
                            >
                              <FiTrash2 />
                            </IconButton>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className={styles.referencesEmpty}>No references yet.</p>
                    )}
                  </div>
                </div>
              </div>
              <div className={styles.customerDivider} />
              <div className={styles.customerModule}>
                <span className={styles.sectionTitle}>Service</span>
                <div className={styles.customerFieldStack}>
                  <div className={styles.fieldLabelStack}>
                    <span className={styles.fieldLabelText}>Equipment types</span>
                    <div className={styles.multiSelect}>
                      <button
                        type="button"
                        className={styles.multiSelectTrigger}
                        ref={equipmentTriggerRef}
                        onClick={() => isInternal && setEquipmentMenuOpen((prev) => !prev)}
                        disabled={!isInternal}
                      >
                        <div className={styles.multiSelectValue}>
                          {selectedEquipmentTypes.length ? (
                            selectedEquipmentTypes.map((type) => (
                              <span key={type} className={styles.multiSelectChip}>
                                {type}
                              </span>
                            ))
                          ) : (
                            <span className={styles.multiSelectPlaceholder}>Select equipment</span>
                          )}
                        </div>
                        <FiChevronDown aria-hidden="true" />
                      </button>
                      {equipmentMenuOpen &&
                        isInternal &&
                        equipmentMenuStyles &&
                        createPortal(
                          <div
                            className={styles.multiSelectMenu}
                            ref={equipmentMenuRef}
                            style={equipmentMenuStyles}
                          >
                            {equipmentOptions.map((option) => {
                              const selected = selectedEquipmentTypes.includes(option);
                              return (
                                <button
                                  key={option}
                                  type="button"
                                  className={`${styles.multiSelectOption} ${
                                    selected ? styles.multiSelectOptionActive : ''
                                  }`}
                                  onClick={() => toggleEquipmentType(option)}
                                >
                                  <span className={styles.multiSelectOptionLabel}>{option}</span>
                                  <span
                                    className={`${styles.multiSelectCheckbox} ${
                                      selected ? styles.multiSelectCheckboxChecked : ''
                                    }`}
                                    aria-hidden="true"
                                  >
                                    <FiCheck />
                                  </span>
                                </button>
                              );
                            })}
                          </div>,
                          document.body
                        )}
                    </div>
                  </div>
                  <div className={styles.fieldLabelStack}>
                    <span className={styles.fieldLabelText}>Equipment requirements</span>
                    <div className={styles.requirementsList}>
                      {selectedEquipmentTypes.length ? (
                        selectedEquipmentTypes.map((type) => {
                          const requirementEntry = equipmentRequirementDetails[type] || {};
                          const lengthValue = requirementEntry.length_ft || '';
                          const selectedSpecialRequirements =
                            requirementEntry.special_requirements || [];
                          const notesValue = requirementEntry.notes || '';
                          const specialRequirementOptions = getSpecialRequirementOptions(type);
                          const hasNotes = Boolean(notesValue.trim());
                          const showWarning =
                            isInternal && type === 'Step Deck' && lengthValue && Number(lengthValue) < 53;
                          return (
                            <div key={type} className={styles.requirementItem}>
                              <div className={styles.requirementRow}>
                                <div className={styles.requirementLabel}>
                                  <span>{type}</span>
                                  {showWarning && (
                                    <span className={styles.warningText}>Recommended 53’</span>
                                  )}
                                </div>
                                <div className={styles.requirementControl}>
                                  <SingleSelectDropdown
                                    value={lengthValue ? String(lengthValue) : ''}
                                    options={equipmentLengthSelectOptions}
                                    placeholder="Length (ft)"
                                    onSelect={(nextValue) => updateEquipmentLength(type, nextValue)}
                                    disabled={!isInternal}
                                  />
                                </div>
                                <div className={styles.requirementControl}>
                                  <div className={styles.multiSelect}>
                                    <button
                                      type="button"
                                      className={styles.multiSelectTrigger}
                                      ref={(node) => {
                                        specialRequirementsTriggerRefs.current[type] = node;
                                      }}
                                      onClick={() =>
                                        isInternal &&
                                        setSpecialRequirementsMenuType((prev) =>
                                          prev === type ? null : type
                                        )
                                      }
                                      disabled={!isInternal}
                                    >
                                      <div className={styles.requirementSelectValue}>
                                        {selectedSpecialRequirements.length ? (
                                          formatSpecialRequirementsValue(type, selectedSpecialRequirements)
                                        ) : (
                                          <span className={styles.multiSelectPlaceholder}>Requirements</span>
                                        )}
                                      </div>
                                      <FiChevronDown aria-hidden="true" />
                                    </button>
                                    {specialRequirementsMenuType === type &&
                                      isInternal &&
                                      specialRequirementsMenuStyles &&
                                      createPortal(
                                        <div
                                          className={styles.multiSelectMenu}
                                          ref={specialRequirementsMenuRef}
                                          style={specialRequirementsMenuStyles}
                                        >
                                          {specialRequirementOptions.length ? (
                                            specialRequirementOptions.map((option) => {
                                              const selected = selectedSpecialRequirements.includes(
                                                option.value
                                              );
                                              return (
                                                <button
                                                  key={option.value}
                                                  type="button"
                                                  className={`${styles.multiSelectOption} ${
                                                    selected ? styles.multiSelectOptionActive : ''
                                                  }`}
                                                  onClick={() => {
                                                    const next = selected
                                                      ? selectedSpecialRequirements.filter(
                                                          (item) => item !== option.value
                                                        )
                                                      : [...selectedSpecialRequirements, option.value];
                                                    updateEquipmentSpecialRequirements(type, next);
                                                  }}
                                                >
                                                  <span className={styles.multiSelectOptionLabel}>
                                                    {option.label}
                                                  </span>
                                                  <span
                                                    className={`${styles.multiSelectCheckbox} ${
                                                      selected ? styles.multiSelectCheckboxChecked : ''
                                                    }`}
                                                    aria-hidden="true"
                                                  >
                                                    <FiCheck />
                                                  </span>
                                                </button>
                                              );
                                            })
                                          ) : (
                                            <div className={styles.requirementMenuEmpty}>
                                              No presets yet
                                            </div>
                                          )}
                                        </div>,
                                        document.body
                                      )}
                                  </div>
                                </div>
                                <EquipmentNotesButton
                                  equipmentType={type}
                                  hasNotes={hasNotes}
                                  onClick={() => setEquipmentNotesEditor(type)}
                                  disabled={!isInternal}
                                />
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <p className={styles.requirementsEmpty}>No equipment selected.</p>
                      )}
                    </div>
                    {equipmentRequirementsError && (
                      <p className={styles.errorText}>{equipmentRequirementsError}</p>
                    )}
                  </div>
                  {isReeferSelected && (
                    <div className={styles.fieldLabelStack}>
                      <span className={styles.fieldLabelText}>Reefer</span>
                      <div className={styles.reeferGrid}>
                        <label>
                          Mode
                          <SingleSelectDropdown
                            value={reeferMode}
                            options={reeferModeOptions}
                            placeholder="Select mode"
                            onSelect={(next) => {
                              setReeferMode(next);
                              commitReeferSettings(next, reeferTemperature, reeferTempClass);
                            }}
                            disabled={!isInternal}
                          />
                        </label>
                        <label>
                          Temp
                          <div className={styles.temperatureField}>
                            <input
                              type="number"
                              value={reeferTemperature}
                              onChange={(event) => {
                                const next = event.target.value;
                                setReeferTemperature(next);
                                scheduleReeferSettings(reeferMode, next, reeferTempClass);
                              }}
                              onBlur={() => scheduleReeferSettings(reeferMode, reeferTemperature, reeferTempClass, true)}
                              placeholder="e.g. 34"
                              disabled={!isInternal}
                            />
                            <span className={styles.temperatureUnit}>°F</span>
                          </div>
                        </label>
                        <label>
                          Temp class
                          <SingleSelectDropdown
                            value={reeferTempClass}
                            options={reeferTempClassOptions}
                            placeholder="Temp class"
                            onSelect={(next) => {
                              setReeferTempClass(next);
                              commitReeferSettings(reeferMode, reeferTemperature, next);
                            }}
                            disabled={!isInternal}
                          />
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className={styles.customerDivider} />
              <div className={styles.customerModule}>
                <span className={styles.sectionTitle}>Commercial</span>
                <div className={styles.customerFieldStack}>
                  <div className={styles.billSection}>
                    <div className={styles.billHeader}>
                      <span className={styles.fieldLabelText}>Customer bill</span>
                      {isInternal && (
                        <Button size="sm" leadingIcon={<FiPlus />} onClick={() => setShowCustomerBillModal(true)}>
                          Bill customer
                        </Button>
                      )}
                    </div>
                    <ul className={styles.costSummary}>
                      {customerCharges.map((item) => (
                        <li key={item.id}>
                          <div>
                            <span className={styles.costLabel}>{item.label}</span>
                            <span className={styles.costMeta}>
                              {item.quantity} × {formatCurrency(item.amount)}
                            </span>
                          </div>
                          <span className={styles.costAmount}>
                            {formatCurrency((item.amount || 0) * (item.quantity || 1))}
                          </span>
                        </li>
                      ))}
                    </ul>
                    <div className={styles.costTotalRow}>
                      <span>Total</span>
                      <span>{formatCurrency(totalCustomerBill)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Surface>

          <Surface padding="xl" className={styles.assignmentCard}>
            <div className={styles.sectionHeader}>
              <h3>Carrier + contacts</h3>
            </div>

            <div className={styles.carrierSection}>
              {hasCarrierSelection ? (
                <div className={styles.carrierPreview}>
                  <div className={styles.carrierPreviewMain}>
                    <div>
                      <p className={styles.carrierName}>{carrierAssignment.name || 'Carrier selected'}</p>
                      <p className={styles.carrierMeta}>
                        {carrierAssignment.mc ? `MC ${carrierAssignment.mc}` : 'MC —'} ·{' '}
                        {carrierAssignment.dot ? `DOT ${carrierAssignment.dot}` : 'DOT —'} ·{' '}
                        {carrierAssignment.scac ? `SCAC ${carrierAssignment.scac}` : 'SCAC —'}
                      </p>
                    </div>
                    <div className={styles.carrierActions}>
                      <button
                        type="button"
                        className={styles.infoIconButton}
                        onClick={handleCarrierInfoClick}
                        title="Carrier profile"
                      >
                        <FiInfo />
                      </button>
                      <IconButton
                        size="sm"
                        variant="ghost"
                        onClick={handleClearCarrier}
                        title="Clear carrier"
                        disabled={assigningCarrier}
                        className={styles.previewIconButton}
                      >
                        <FiX />
                      </IconButton>
                    </div>
                  </div>
                </div>
              ) : (
                <div className={styles.carrierSearchPanel}>
                  <label className={styles.fieldLabel} htmlFor="carrier-search">Carrier</label>
                  <div className={styles.carrierSearchRow}>
                    <input
                      id="carrier-search"
                      value={carrierSearch}
                      onChange={handleCarrierSearchChange}
                      onKeyDown={handleCarrierSearchKeyDown}
                      autoComplete="off"
                      autoCorrect="off"
                      spellCheck={false}
                      placeholder="Search by MC, DOT, or name"
                    />
                    {pendingCarrier && (
                      <Button
                        size="sm"
                        onClick={handleAssignCarrier}
                        disabled={assigningCarrier}
                      >
                        {assigningCarrier ? 'Adding…' : 'Add carrier'}
                      </Button>
                    )}
                  </div>
                  {carrierSearch.trim() && !hasSelectedOption ? (
                    shouldShowCarrierDropdown ? (
                      <div className={styles.carrierDropdown}>
                        {isCarrierLoading && <p className={styles.assignmentHint}>Searching carriers…</p>}
                        {!isCarrierLoading &&
                          visibleCarrierResults.map((carrier, index) => (
                            <button
                              key={carrier.id}
                              type="button"
                              className={`${styles.carrierResult} ${
                                pendingCarrier?.id === carrier.id || highlightedIndex === index
                                  ? styles.carrierResultActive
                                  : ''
                              }`}
                              onClick={() => handleCarrierSelect(carrier)}
                            >
                              <div>
                                <p className={styles.carrierName}>{carrier.name || carrier.company_name || 'Carrier'}</p>
                                <p className={styles.carrierMeta}>
                                  {`MC ${carrier.mc || '—'} · DOT ${carrier.dot || '—'}${
                                    carrier.scac ? ` · SCAC ${carrier.scac}` : ''
                                  }`}
                                </p>
                              </div>
                            </button>
                          ))}
                      </div>
                    ) : (
                      !isCarrierLoading && <p className={styles.assignmentHint}>No carriers found.</p>
                    )
                  ) : (
                    <p className={styles.assignmentHint}>
                      {pendingCarrier
                        ? 'Click “Add carrier” to confirm this selection.'
                        : 'Start typing to search carrier directory.'}
                    </p>
                  )}
                </div>
              )}
            </div>

            {canViewCarrierFinancials && <div className={styles.assignmentSection}>
              <span className={styles.sectionTitle}>Dispatcher</span>
              {hasDispatcher && !isAddingDispatcher ? (
                <div className={styles.contactPreview}>
                  <div>
                    <p className={styles.carrierName}>{dispatcher.name || 'Dispatcher'}</p>
                    <p className={styles.carrierMeta}>
                      {dispatcher.phone || 'No phone'}
                      {dispatcher.extension ? ` ext. ${dispatcher.extension}` : ''}
                    </p>
                    {dispatcher.email && <p className={styles.carrierMeta}>{dispatcher.email}</p>}
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => setIsAddingDispatcher(true)}>
                    Edit
                  </Button>
                </div>
              ) : (
                <>
                  {!isAddingDispatcher && (
                    <Button size="sm" variant="ghost" onClick={() => setIsAddingDispatcher(true)}>
                      + Add dispatcher
                    </Button>
                  )}
                  {isAddingDispatcher && (
                    <>
                      <div className={styles.assignmentGrid}>
                        <div className={`${styles.contactAutocomplete} ${styles.fieldWithError}`}>
                          <input
                            name="ref_disp_q77"
                            value={dispatcher.name}
                            className={dispatcherErrors.name ? styles.inputError : ''}
                            onChange={(event) => {
                              clearDispatcherFieldError('name');
                              setDispatcher({ ...dispatcher, name: event.target.value });
                            }}
                            placeholder="Name"
                            autoComplete="off-random"
                            autoCorrect="off"
                            spellCheck={false}
                            aria-autocomplete="list"
                            role="combobox"
                            data-lpignore="true"
                            onFocus={() => {
                              clearTimeout(dispatcherBlurTimeout.current);
                              setDispatcherDropdownOpen(true);
                            }}
                            onBlur={() => {
                              dispatcherBlurTimeout.current = setTimeout(
                                () => setDispatcherDropdownOpen(false),
                                120
                              );
                            }}
                          />
                          {dispatcherDropdownOpen && (
                            <div className={styles.contactDropdown}>
                              <div className={styles.contactDropdownList}>
                                {(filteredDispatcherOptions.length
                                  ? filteredDispatcherOptions
                                  : dispatcherOptions
                                )
                                  .slice(0, 6)
                                  .map((option) => (
                                    <button
                                      key={`${option.name}-${option.phone}-${option.email}-${option.extension || ''}`}
                                      type="button"
                                      className={styles.contactOption}
                                      onMouseDown={(event) => {
                                        event.preventDefault();
                                        clearTimeout(dispatcherBlurTimeout.current);
                                        applyDispatcherContact(option);
                                      }}
                                    >
                                      <span className={styles.contactOptionName}>{option.name}</span>
                                      <span className={styles.contactOptionMeta}>
                                        {option.phone || 'No phone'}
                                        {option.extension ? ` • ext ${option.extension}` : ''}
                                        {option.email ? ` • ${option.email}` : ''}
                                      </span>
                                    </button>
                                  ))}
                              </div>
                              <button
                                type="button"
                                className={`${styles.contactOption} ${styles.addContactOption}`}
                                onMouseDown={(event) => {
                                  event.preventDefault();
                                  clearTimeout(dispatcherBlurTimeout.current);
                                  dispatcherManualEntry.current = true;
                                  setDispatcher((prev) => ({
                                    ...prev,
                                    role: 'Dispatcher',
                                    phone: '',
                                    extension: '',
                                    email: ''
                                  }));
                                  setDispatcherDropdownOpen(false);
                                }}
                              >
                                + Add new contact
                              </button>
                            </div>
                          )}
                          {dispatcherErrors.name && (
                            <p className={styles.errorText}>{dispatcherErrors.name}</p>
                          )}
                        </div>
                        <div className={styles.fieldWithError}>
                          <input
                            name="ref_ph_11"
                            value={dispatcher.phone}
                            className={dispatcherErrors.phone ? styles.inputError : ''}
                            onChange={(event) => {
                              clearDispatcherFieldError('phone');
                              handleDispatcherPhoneChange(event.target.value);
                            }}
                            onBlur={handleDispatcherPhoneBlur}
                            placeholder="Phone"
                            autoComplete="nope"
                            role="presentation"
                            data-lpignore="true"
                          />
                          {dispatcherErrors.phone && (
                            <p className={styles.errorText}>{dispatcherErrors.phone}</p>
                          )}
                        </div>
                        <div className={styles.fieldWithError}>
                          <input
                            value={dispatcher.extension ?? ''}
                            onChange={(event) => setDispatcher({ ...dispatcher, extension: event.target.value })}
                            placeholder="Ext"
                          />
                        </div>
                        <div className={styles.fieldWithError}>
                          <input
                            value={dispatcher.email}
                            className={dispatcherErrors.email ? styles.inputError : ''}
                            onChange={(event) => {
                              clearDispatcherFieldError('email');
                              setDispatcher({ ...dispatcher, email: event.target.value });
                            }}
                            placeholder="Email"
                          />
                          {dispatcherErrors.email && (
                            <p className={styles.errorText}>{dispatcherErrors.email}</p>
                          )}
                        </div>
                      </div>
                      <div className={styles.inlineActions}>
                        <Button size="sm" onClick={handleDispatcherSave}>Save dispatcher</Button>
                        <Button size="sm" variant="ghost" onClick={() => setIsAddingDispatcher(false)}>
                          Cancel
                        </Button>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>}

            <div className={styles.assignmentSection}>
              <span className={styles.sectionTitle}>Driver</span>
              {hasDriver && !isAddingDriver ? (
                <div className={styles.contactPreview}>
                  <div>
                    <p className={styles.carrierName}>{driver.name || 'Driver'}</p>
                    <p className={styles.carrierMeta}>{driver.phone || 'No mobile'}</p>
                    <p className={styles.carrierMeta}>
                      {driver.truck ? `Truck ${driver.truck}` : 'Truck —'} · {driver.trailer ? `Trailer ${driver.trailer}` : 'Trailer —'}
                    </p>
                    {driver.vin && (
                      <p className={`${styles.carrierMeta} ${styles.vinValue}`} title={driver.vin}>
                        VIN {driver.vin}
                      </p>
                    )}
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => setIsAddingDriver(true)}>
                    Edit
                  </Button>
                </div>
              ) : (
                <>
                  {!isAddingDriver && (
                    <Button size="sm" variant="ghost" onClick={() => setIsAddingDriver(true)}>
                      + Add driver
                    </Button>
                  )}
                  {isAddingDriver && (
                    <>
                      <div className={styles.assignmentGrid}>
                        <div className={`${styles.contactAutocomplete} ${styles.fieldWithError}`}>
                          <input
                            name="ref_drv_x99"
                            value={driver.name}
                            className={driverErrors.name ? styles.inputError : ''}
                            onChange={(event) => {
                              clearDriverFieldError('name');
                              setDriver({ ...driver, name: event.target.value });
                            }}
                            placeholder="Name"
                            autoComplete="off-random"
                            autoCorrect="off"
                            spellCheck={false}
                            aria-autocomplete="list"
                            role="combobox"
                            data-lpignore="true"
                            onFocus={() => {
                              clearTimeout(driverBlurTimeout.current);
                              setDriverDropdownOpen(true);
                            }}
                            onBlur={() => {
                              driverBlurTimeout.current = setTimeout(() => setDriverDropdownOpen(false), 120);
                            }}
                          />
                          {driverDropdownOpen && (
                            <div className={styles.contactDropdown}>
                              <div className={styles.contactDropdownList}>
                                {(filteredDriverOptions.length ? filteredDriverOptions : driverOptions)
                                  .slice(0, 6)
                                  .map((option) => (
                                    <button
                                      key={`${option.name}-${option.phone}`}
                                      type="button"
                                      className={styles.contactOption}
                                      onMouseDown={(event) => {
                                        event.preventDefault();
                                        clearTimeout(driverBlurTimeout.current);
                                        applyDriverContact(option);
                                      }}
                                    >
                                      <span className={styles.contactOptionName}>{option.name}</span>
                                      <span className={styles.contactOptionMeta}>
                                        {option.phone || 'No phone'}
                                      </span>
                                    </button>
                                  ))}
                              </div>
                              <button
                                type="button"
                                className={`${styles.contactOption} ${styles.addContactOption}`}
                                onMouseDown={(event) => {
                                  event.preventDefault();
                                  clearTimeout(driverBlurTimeout.current);
                                  driverManualEntry.current = true;
                                  setDriver((prev) => ({
                                    ...prev,
                                    role: 'Driver',
                                    phone: '',
                                    truck: prev.truck,
                                    trailer: prev.trailer,
                                    vin: prev.vin
                                  }));
                                  setDriverDropdownOpen(false);
                                }}
                              >
                                + Add new contact
                              </button>
                            </div>
                          )}
                          {driverErrors.name && <p className={styles.errorText}>{driverErrors.name}</p>}
                        </div>
                        <div className={styles.fieldWithError}>
                          <input
                            name="ref_mob_22"
                            value={driver.phone}
                            className={driverErrors.phone ? styles.inputError : ''}
                            onChange={(event) => {
                              clearDriverFieldError('phone');
                              handleDriverPhoneChange(event.target.value);
                            }}
                            onBlur={handleDriverPhoneBlur}
                            placeholder="Mobile"
                            autoComplete="nope"
                            role="presentation"
                            data-lpignore="true"
                          />
                          {driverErrors.phone && <p className={styles.errorText}>{driverErrors.phone}</p>}
                        </div>
                        <div className={styles.fieldWithError}>
                          <input
                            value={driver.truck}
                            className={driverErrors.truck ? styles.inputError : ''}
                            onChange={(event) => {
                              clearDriverFieldError('truck');
                              setDriver({ ...driver, truck: event.target.value });
                            }}
                            placeholder="Truck #"
                          />
                          {driverErrors.truck && <p className={styles.errorText}>{driverErrors.truck}</p>}
                        </div>
                        <div className={styles.fieldWithError}>
                          <input
                            value={driver.trailer ?? ''}
                            onChange={(event) => setDriver({ ...driver, trailer: event.target.value })}
                            placeholder="Trailer #"
                          />
                        </div>
                        <div className={styles.fieldWithError}>
                          <input
                            value={driver.vin ?? ''}
                            onChange={(event) => setDriver({ ...driver, vin: event.target.value })}
                            placeholder="VIN"
                          />
                        </div>
                      </div>
                      <div className={styles.inlineActions}>
                        <Button size="sm" onClick={handleDriverSave}>Save driver</Button>
                        <Button size="sm" variant="ghost" onClick={() => setIsAddingDriver(false)}>
                          Cancel
                        </Button>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>

            <div className={styles.assignmentDivider} />

            <div className={styles.assignmentSection}>
              <div className={styles.sectionHeader}>
                <span className={styles.sectionTitle}>Carrier pay</span>
                <Button size="sm" leadingIcon={<FiPlus />} onClick={() => setShowCarrierPayModal(true)}>
                  Pay carrier
                </Button>
              </div>
              <ul className={styles.costSummary}>
                {costLineItems.map((item) => (
                  <li key={item.id}>
                    <div>
                      <span className={styles.costLabel}>{item.label}</span>
                      <span className={styles.costMeta}>
                        {item.quantity} × {formatCurrency(item.amount)}
                      </span>
                    </div>
                    <span className={styles.costAmount}>{formatCurrency((item.amount || 0) * (item.quantity || 1))}</span>
                  </li>
                ))}
              </ul>
              <div className={styles.costTotalRow}>
                <span>Total</span>
                <span>{formatCurrency(totalCarrierPay)}</span>
              </div>
            </div>
          </Surface>

          <Surface padding="xl" className={styles.mapCard}>
            <div className={styles.sectionHeader}>
              <h3>Route overview</h3>
              <IconButton size="sm" onClick={() => setShowTrackingModal(true)} title="Send tracking">
                <FiTarget />
              </IconButton>
            </div>
            <div className={styles.mapPlaceholder}>
              <div className={styles.mapContent}>
                <div className={styles.routeHeader}>
                  <div className={styles.routeSummaryLine}>
                    <span className={styles.routeSummary}>
                      {routeOrigin} →
                    </span>
                    <span className={styles.routeSummaryHighlight}>
                      {routeDestination}
                    </span>
                  </div>
                  <div className={styles.routeDates}>
                    {pickupDateDisplay} → {deliveryDateDisplay}
                  </div>
                </div>
                <span className={styles.mapMeta}>Carrier position appears once tracking is active.</span>
              </div>
            </div>
            <div className={styles.routeTimelineSection}>
              {hasTimelineStops ? (
                <RouteOverviewTimeline stops={timelineStops} etaNodeIndex={etaNodeIndex} miles={headerMiles} />
              ) : (
                <div className={styles.timelinePlaceholder}>
                  Route timeline will appear once pickup and delivery are set.
                </div>
              )}
            </div>
            <div className={styles.mapFooter}>
              <Button variant="ghost" size="sm" leadingIcon={<FiMap />}>
                View in Maps
              </Button>
              <span className={styles.mapStatus}>{driverTrackingPrompt}</span>
            </div>
          </Surface>
        </div>

        <div className={styles.bodyLayout}>
          <div className={styles.mainColumn}>
          <Surface padding="xl">
            <div className={styles.sectionHeaderStack}>
              <h3>Pickups</h3>
              <Button
                size="sm"
                variant="ghost"
                className={styles.inlineAdd}
                leadingIcon={<FiPlus />}
                onClick={() => promptLocationSelect('pickup')}
              >
                Add pickup
              </Button>
            </div>
            {renderStopTable('pickup', pickupStops)}
          </Surface>

          <Surface padding="xl">
            <div className={styles.sectionHeaderStack}>
              <h3>Deliveries</h3>
              <Button
                size="sm"
                variant="ghost"
                className={styles.inlineAdd}
                leadingIcon={<FiPlus />}
                onClick={() => promptLocationSelect('delivery')}
              >
                Add delivery
              </Button>
            </div>
            {renderStopTable('delivery', deliveryStops)}
          </Surface>
          <Surface padding="xl">
            <div className={styles.sectionHeaderStack}>
              <h3>Commodity</h3>
              <Button
                size="sm"
                variant="ghost"
                className={styles.inlineAdd}
                leadingIcon={<FiPlus />}
                onClick={addCommodity}
              >
                Add commodity
              </Button>
            </div>
            {commodityRows.length > 0 ? (
              <div className={styles.tableWrapper}>
                <table className={styles.gridTable}>
                    <thead>
                      <tr>
                        <th>Description</th>
                        <th>Quantity</th>
                        <th>Unit</th>
                        <th>Weight</th>
                        <th>Notes</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {commodityRows.map((item, index) => (
                        <tr key={item.id}>
                          <td>
                            <input
                              value={item.description}
                              onChange={(event) => updateCommodity(index, 'description', event.target.value)}
                              placeholder="Commodity"
                            />
                          </td>
                          <td>
                            <input
                              value={item.quantity}
                              onChange={(event) => updateCommodity(index, 'quantity', event.target.value)}
                              placeholder="Qty"
                            />
                          </td>
                          <td>
                            <input
                              value={item.unit}
                              onChange={(event) => updateCommodity(index, 'unit', event.target.value)}
                              placeholder="Unit"
                            />
                          </td>
                          <td>
                            <input
                              value={item.weight}
                              onChange={(event) => updateCommodity(index, 'weight', event.target.value)}
                              placeholder="Weight"
                            />
                          </td>
                          <td>
                            <input
                              value={item.notes}
                              onChange={(event) => updateCommodity(index, 'notes', event.target.value)}
                              placeholder="Notes"
                            />
                          </td>
                          <td className={styles.actionsCell}>
                            <IconButton size="sm" onClick={() => removeCommodity(index)}>
                              <FiTrash2 />
                            </IconButton>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className={styles.emptyState}>No commodities added yet.</div>
              )}
            </Surface>

          <Surface padding="xl">
            <div className={styles.sectionHeaderStack}>
              <h3>Activity & Remarks</h3>
              <Button
                size="sm"
                variant="ghost"
                className={styles.inlineAdd}
                onClick={() => startRemarkDraftEditing('remark')}
                disabled={isAddingRemark}
              >
                Add remark
              </Button>
            </div>
            <p className={styles.activityHint}>
              Date, author, and miles fill automatically. Enter a location and required remark to log
              activity.
            </p>
            <div className={`${styles.tableWrapper} ${styles.activityScrollWrapper}`}>
              <table className={`${styles.gridTable} ${styles.activityTable}`}>
                <thead>
                  <tr>
                    <th className={styles.activityDateCol}>Date</th>
                    <th className={styles.activityAuthorCol}>Author</th>
                    <th className={styles.activityLocationCol}>Location</th>
                    <th className={styles.activityMilesCol}>Miles</th>
                    <th className={styles.activityRemarkCol}>Remark</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    className={`${styles.interactiveRow} ${styles.activityRow} ${styles.newRemarkRow} ${
                      remarkDraft.isEditing ? styles.activeEditRow : ''
                    }`}
                    onDoubleClick={() => startRemarkDraftEditing('remark')}
                  >
                    <td className={`${styles.activityTimestamp} ${styles.activityDateCol}`}>
                      <span className={styles.activityCellValue}>{draftDateDisplay}</span>
                    </td>
                    <td className={styles.activityAuthorCol}>
                      <span className={styles.activityCellValue}>{remarkDraft.author || '—'}</span>
                    </td>
                    <td className={`${styles.activityLocationCol} ${styles.editableCell}`}>
                      {/* TODO: integrate Google Places-style search for structured locations. */}
                      {remarkDraft.isEditing ? (
                        <input
                          ref={remarkLocationInputRef}
                          value={remarkDraft.location}
                          readOnly={!remarkDraft.isEditing}
                          onFocus={() => {
                            if (!remarkDraft.isEditing) {
                              startRemarkDraftEditing('location');
                            }
                          }}
                          onChange={(event) => handleRemarkDraftChange('location', event.target.value)}
                          className={styles.activityCellInput}
                          placeholder="—"
                        />
                      ) : (
                        <button
                          type="button"
                          className={`${styles.activityCellPlaceholderButton} ${styles.activityCellValue}`}
                          onClick={() => startRemarkDraftEditing('location')}
                          aria-label="Add location"
                        >
                          —
                        </button>
                      )}
                    </td>
                    <td className={styles.activityMilesCol}>
                      {/* TODO: Future: populate miles from tracking API and shipment status (DISPATCHED-NOT-LOADED vs OTR-OTW). */}
                      <span className={`${styles.activityCellValue} ${styles.milesPlaceholder}`}>—</span>
                    </td>
                    <td className={`${styles.activityRemarkCol} ${styles.remarkCell} ${styles.editableCell}`}>
                      <div className={styles.remarkInputShell}>
                        <input
                          ref={remarkInputRef}
                          value={remarkDraft.note}
                          onFocus={() => {
                            if (!remarkDraft.isEditing) {
                              startRemarkDraftEditing('remark');
                            }
                          }}
                          onChange={(event) => handleRemarkDraftChange('note', event.target.value)}
                          className={`${styles.remarkScroller} ${styles.activityCellInput}`}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              event.preventDefault();
                              addRemark();
                            }
                            if (event.key === 'Escape') {
                              cancelRemarkDraft();
                            }
                          }}
                        />
                        <IconButton
                          size="sm"
                          variant="ghost"
                          className={styles.cancelRemarkButton}
                          onClick={cancelRemarkDraft}
                          tabIndex={-1}
                          title="Discard remark"
                          aria-label="Discard remark"
                        >
                          <FiX />
                        </IconButton>
                      </div>
                      {remarkDraft.error && <div className={styles.inlineError}>{remarkDraft.error}</div>}
                    </td>
                  </tr>
                  {orderedRemarks.length > 0
                    ? orderedRemarks.map((entry) => (
                        <tr key={entry.id} className={`${styles.interactiveRow} ${styles.activityRow}`}>
                          <td className={`${styles.activityTimestamp} ${styles.activityDateCol}`}>
                            {formatActivityTimestamp(entry.rawTimestamp, entry.timestamp)}
                          </td>
                          <td className={styles.activityAuthorCol}>{entry.author || currentUserName}</td>
                          <td className={styles.activityLocationCol}>{entry.location || '—'}</td>
                          <td className={styles.activityMilesCol}>
                            {entry.miles || entry.miles === 0 ? entry.miles : '—'}
                          </td>
                          <td className={`${styles.activityRemarkCol} ${styles.remarkCell}`}>
                            <span className={styles.remarkOverflow}>{entry.note || '—'}</span>
                          </td>
                        </tr>
                      ))
                    : null}
                  <tr className={`${styles.interactiveRow} ${styles.activityRow} ${styles.blankRemarkRow}`} aria-hidden>
                    <td className={`${styles.activityTimestamp} ${styles.activityDateCol}`}>—</td>
                    <td className={styles.activityAuthorCol}>—</td>
                    <td className={styles.activityLocationCol}>—</td>
                    <td className={styles.activityMilesCol}>—</td>
                    <td className={`${styles.activityRemarkCol} ${styles.blankRemarkCell}`}>—</td>
                  </tr>
                  {orderedRemarks.length === 0 ? (
                    <tr
                      className={`${styles.interactiveRow} ${styles.activityRow} ${styles.blankRemarkRow}`}
                      aria-hidden
                    >
                      <td className={`${styles.activityTimestamp} ${styles.activityDateCol}`}>—</td>
                      <td className={styles.activityAuthorCol}>—</td>
                      <td className={styles.activityLocationCol}>—</td>
                      <td className={styles.activityMilesCol}>—</td>
                      <td className={`${styles.activityRemarkCol} ${styles.blankRemarkCell}`}>—</td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </Surface>
          </div>

          <div className={styles.secondaryColumn}>
            <Surface padding="xl">
              <div className={styles.sectionHeader}>
                <h3>Load health</h3>
              </div>
              <ul className={styles.healthList}>
                <li>
                  <strong>Instructions</strong>
                  <span>{specialInstructions.length > 0 ? `${specialInstructions.length} noted` : 'None logged'}</span>
                </li>
                <li>
                  <strong>Documents</strong>
                  <span>Managed in console</span>
                </li>
                <li>
                  <strong>Tracking</strong>
                  <span>{trackingEntries.length ? `${trackingEntries.length} pings` : 'Awaiting first ping'}</span>
                </li>
              </ul>
            </Surface>

            <Surface padding="xl">
              <div className={styles.sectionHeader}>
                <h3>Scheduling & Contact Control</h3>
              </div>
              <div className={styles.contactStack}>
                {formattedStops.map((stop, index) => {
                  const location = stop.location || {};
                  const appointmentEmails = getAppointmentEmails(location);
                  const dockPhones = getDockPhones(location);
                  const isAppointmentStop =
                    Boolean(stop.appointmentRequired) || (location.dock_type || '').toLowerCase() === 'appointment';
                  const isAppointmentDockType = (location.dock_type || '').toLowerCase() === 'appointment';
                  const isFcfsStop = (location.dock_type || '').toLowerCase() === 'fcfs' || !isAppointmentStop;
                  const locationLabel = location.name || location.company || `${stop.type} location`;
                  const locationCityLine = [location.city, location.state].filter(Boolean).join(', ');
                  const showSchedulingEmailButton = isAppointmentDockType && appointmentEmails.length > 0;

          const renderContactValue = (values, contextTitle) => {
            if (!values.length) {
              return (
                <div className={styles.contactValue}>
                  <span className={styles.quietText}>No contacts available</span>
                  <button
                    type="button"
                    className={styles.linkButton}
                    onClick={() =>
                      setContactListModal({
                        title: `${locationLabel} contacts`,
                        items: [],
                        emptyMessage: 'Location profile coming soon.'
                      })
                    }
                  >
                    +add contacts
                  </button>
                </div>
              );
            }
            return (
              <div className={styles.contactValue}>
                <span className={styles.quietText}>{values[0]}</span>
                {values.length > 1 && (
                  <button
                    type="button"
                    className={styles.linkButton}
                    onClick={() =>
                      setContactListModal({
                        title: contextTitle,
                        items: values
                      })
                    }
                  >
                    + more
                  </button>
                )}
              </div>
            );
          };

          return (
            <div key={stop.id || `${stop.type}-${index}`} className={styles.contactCard}>
              <div className={styles.contactCardHeader}>
                <div className={styles.contactSummary}>
                  <p className={styles.contactMeta}>{stop.type}</p>
                  <p>
                    <strong>{locationLabel}</strong>
                  </p>
                  {locationCityLine && <p className={styles.contactMeta}>{locationCityLine}</p>}
                </div>
                <span className={styles.contactPill}>{isAppointmentStop ? 'Appointment' : 'FCFS'}</span>
              </div>

              <div className={styles.contactRows}>
                {isAppointmentStop && (
                  <div className={styles.contactRow}>
                    <span className={styles.contactLabel}>Scheduling email</span>
                    {renderContactValue(appointmentEmails, `${locationLabel} scheduling emails`)}
                  </div>
                )}

                {isFcfsStop && (
                  <div className={styles.contactRow}>
                    <span className={styles.contactLabel}>Dock contact</span>
                    {renderContactValue(dockPhones, `${locationLabel} dock contacts`)}
                  </div>
                )}
              </div>

              {showSchedulingEmailButton && (
                <div className={styles.contactActions}>
                  <Button
                    size="sm"
                    variant="secondary"
                    leadingIcon={<FiMail />}
                    onClick={() => setShowSchedulingEmailPlaceholder(true)}
                  >
                    Prepare scheduling email
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Surface>
      </div>
        </div>

        <div className={styles.bottomDivider} />
        </>}
        </div>
        </main>

        <aside className={`${styles.sideRail} ${sideRailExpanded ? styles.sideRailExpanded : ''}`}>
          <button
            type="button"
            className={styles.sideRailToggle}
            onClick={() => setSideRailExpanded((prev) => !prev)}
          >
            {sideRailExpanded ? <FiChevronRight /> : <FiChevronLeft />}
          </button>
          <div className={styles.sideRailButtons}>
            <IconButton
              size="sm"
              active={activeUtility === 'leadScript'}
              onClick={() => {
                setSideRailExpanded(true);
                setActiveUtility('leadScript');
              }}
              title="Lead script"
            >
              <FiList />
            </IconButton>
            <IconButton
              size="sm"
              active={activeUtility === 'checklist'}
              onClick={() => {
                setSideRailExpanded(true);
                setActiveUtility('checklist');
              }}
              title="Checklist"
            >
              <FiCheckSquare />
            </IconButton>
            <IconButton size="sm" title="Mark claim">
              <FiInfo />
            </IconButton>
            <IconButton
              size="sm"
              active={activeUtility === 'messaging'}
              onClick={() => {
                setSideRailExpanded(true);
                setActiveUtility('messaging');
              }}
              title="Text driver"
            >
              <FiSend />
            </IconButton>
          </div>
          <div className={styles.sideRailContent}>{renderUtilityPanel()}</div>
        </aside>

      {showTrackingModal && renderTrackingModal()}
      {showCarrierPayModal && renderCarrierPayModal()}
      {showCustomerBillModal && renderCustomerBillModal()}
      {equipmentNotesEditor && (
        <EquipmentNotesModal
          key={equipmentNotesEditor}
          equipmentType={equipmentNotesEditor}
          initialValue={equipmentRequirementDetails[equipmentNotesEditor]?.notes || ''}
          onCancel={() => setEquipmentNotesEditor(null)}
          onSave={(value) => saveEquipmentNotes(equipmentNotesEditor, value)}
        />
      )}
      {contactListModal && (
        <div className={styles.modalBackdrop}>
          <div className={styles.modal} role="dialog" aria-modal="true">
            <header className={styles.modalHeader}>
              <h3>{contactListModal.title || 'Location contacts'}</h3>
              <Button variant="ghost" leadingIcon={<FiX />} onClick={() => setContactListModal(null)}>
                Close
              </Button>
            </header>
            <div className={styles.modalBodyVertical}>
              {contactListModal.items?.length ? (
                <ul className={styles.contactListModal}>
                  {contactListModal.items.map((item, idx) => (
                    <li key={`${item}-${idx}`} className={styles.contactListItem}>
                      {item}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className={styles.modalHint}>{contactListModal.emptyMessage || 'No contacts listed.'}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {showSchedulingEmailPlaceholder && (
        <div className={styles.modalBackdrop}>
          <div className={styles.modal} role="dialog" aria-modal="true">
            <header className={styles.modalHeader}>
              <h3>Prepare scheduling email</h3>
              <Button
                variant="ghost"
                leadingIcon={<FiX />}
                onClick={() => setShowSchedulingEmailPlaceholder(false)}
              >
                Close
              </Button>
            </header>
            <div className={styles.modalBodyVertical}>
              <p className={styles.modalHint}>Email automation coming soon.</p>
              <div className={styles.modalActions}>
                <Button onClick={() => setShowSchedulingEmailPlaceholder(false)}>Got it</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <datalist id="time-options">
        {TIME_OPTIONS.map((time) => (
          <option key={time} value={time} />
        ))}
        </datalist>
      </div>
    </div>
  );
};

export default LoadWorkspace;
